/**
 * AppsPage.tsx — App 管理页（list / detail，含全套配置）
 *
 * 移植自原型 design/Novel-admin/src/pages/apps.jsx 的 AppsPage + AppCard + AppDetail 及各 Detail* 小节。
 * 写路径已接真（goal PR4）：
 *   - 暂停 / 恢复运营 → POST /apps/:id/disable|enable（单一入口在「高级操作」标签 DetailDanger，requireTyping 二次确认；
 *     Overview 运营状态仅只读展示 chip，不再做第二启停入口）
 *   - 保存配置 → PATCH /apps/:id（详情 5 tab 受控表单，初值来自 GET /apps/:id 真 AppResponse）
 *   - 支付渠道 → GET /apps/:id/payment-methods 后按 IAP/STRIPE 汇总为只读状态；场景级启停在 Paywall 管理
 *   - 导出配置 JSON → 无导出端点（dev-docs 未定义）：GET /apps/:id 取真 DTO 纯前端生成下载
 *   - 删除 App → 无契约端点（dev-docs ADM-APP 无删除需求 / server 无 DELETE）→ 菜单项 disabled，
 *     见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五
 * GET /apps 已携带真实 book_count，adapter 直接映射为 books，0 是有效可见书组数；仅 users / dau /
 * revenue / refundRate 与渠道收入分布仍无统计端点，未注入即 undefined，UI 渲染 '—'。原 KPI 环比 delta
 * 伪指标块已移除（无 per-app 时序源）。平台筛选下推到 canonical apps.platform 查询参数。
 * 视觉层（className / inline-style）与原型 1:1 保真（上述接真偏离已记账计划 §五 + docs/plans/2026-06-23-web-honest-degradation.md）。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  type AppPlatform,
  DEFAULT_REWARDED_AD_CONFIG,
  type HomeScreenConfig,
  HomeScreenConfigSchema,
  type TaskCenterTaskDisplayEntry,
  TaskCenterTaskDisplaySchema,
  type UpdateAppRequest,
  UpdateAppRequestSchema,
} from '@novel-admin/shared';
import { AdminApiError } from '~/api/errors';
import { useAppScope } from '~/app/app-scope-store';
import { summarizeAppPaymentMethods } from '~/data/adapters/app-payment-methods-adapter';
import {
  type AppPaymentMethodResponse,
  type AppResponse,
  type AppRow,
  type TaskCenterRewards,
  appQueryKeys,
  useAppDetail,
  useAppPaymentMethods,
  useApps,
  useSysConfig,
} from '~/data/queries';
import { disableApp, enableApp, getApp, updateApp } from '~/hooks/use-apps';
import { fmtCountry, fmtDate, fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { AppIcon } from '~/ui/AppIcon';
import { ContextMenu } from '~/ui/ContextMenu';
import { ConfirmDialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { FormRow } from '~/ui/FormRow';
import { SearchInput } from '~/ui/SearchInput';
import { Section } from '~/ui/Section';
import { CardGridSkeleton, DetailSkeleton } from '~/ui/Skeleton';
import { SortableList } from '~/ui/SortableList';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { Charts } from '~/ui/charts';
import { PaymentChannelReadinessPanel } from './PaymentChannelReadinessPanel';
import { PaymentMethodIconsPanel } from './PaymentMethodIconsPanel';
import {
  PAYMENT_METHOD_ICONS_ANCHOR_ID,
  PaymentMethodPoliciesPanel,
} from './PaymentMethodPoliciesPanel';
import { ReadingPassLanguageConfigsPanel } from './ReadingPassLanguageConfigsPanel';
import {
  ReadingPassReminderFields,
  formatReadingPassError,
  readingPassReminderIssue,
} from './ReadingPassReminderFields';
import { RewardedAdsPanel } from './RewardedAdsPanel';
import {
  APP_CODE_FORMAT_HINT,
  TASK_CENTER_TASK_DISPLAY_LABELS,
  appCodeInputIssue,
  appCodeServerIssue,
  normalizeAppCodeDraft,
  normalizeAppCodeInput,
  parseTaskCenterRewards,
  parseTaskCenterTaskDisplay,
  pctToRatio,
  ratioToPct,
} from './app-config-form-mappers';
import { CopyAppDialog } from './dialogs/CopyAppDialog';
import { NewAppWizard } from './dialogs/NewAppWizard';
import {
  type RewardedAdConfigDraft,
  RewardedAdConfigFields,
  type RewardedAdFieldErrors,
  rewardedAdConfigFromDraft,
  rewardedAdConfigToDraft,
} from './rewarded-ad-config-fields';

// ---------- 本页弹窗 open-state（取代原型 window.openDialog hub）----------
/** 通用二次确认弹窗配置（对应原型 openDialog('confirm', props)） */
interface ConfirmConfig {
  title: ReactNode;
  message: ReactNode;
  tone?: 'danger' | 'warn' | 'default';
  icon?: keyof typeof Icons;
  confirmLabel?: string;
  cancelLabel?: string;
  requireTyping?: string;
  onConfirm?: () => void;
}
/** 本页可打开的弹窗：新建 App / 复制 App / 通用确认 */
type DialogState =
  | { kind: 'new-app' }
  | { kind: 'copy-app'; source?: AppRow }
  | { kind: 'confirm'; config: ConfirmConfig }
  | null;

// Context for AppDetail dirty-state — child sections call markDirty() when user edits
const AppDirtyCtx = createContext<() => void>(() => {});
const useMarkDirty = () => useContext(AppDirtyCtx);

// 2026-05-28 契约同步：App 层删除 market_timezone（用户日切时区下沉到 users.day_cut_timezone，
// 由首次匿名登录服务端绑定，admin 不交互）
// 运营语言 → 允许语言集合：en 必须包含，作为请求语言越界时的 fallback 边界
// 千字单价 → 限于“创建 novel 时的初始 fallback 默认值”，实际定价下沉至 per-novel

/** AdminApiError / Error → 用户可读文案 */
function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : '操作失败';
}

/**
 * 前端 RBAC 防线（后端 requireRole 才是权威，详见 server routes/v1/apps）：
 * - 生命周期 / 复制 / 创建 / **配置编辑**（POST /apps · /apps/:id/copy · /apps/:id/enable|disable · PATCH /apps/:id）
 *   = root / admin —— App 编辑高危写、影响 C 端全量（dev-docs AB-运营应用.md §4；operator 无 App 域权限，#304）
 * viewer 一律只读。各 hook 订阅 role，保证写控件按角色 disabled、越权点击零写请求。
 */
function useCanManageApp(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}
// 配置编辑（PATCH /apps/:id，含 default_language / 两个 App 级渠道开关）= root/admin（#304 收窄，去 operator）
function useCanEditAppConfig(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}
/** 写权限不足的菜单项 title（与 server requireRole 一致；指向后端权威） */
const MANAGE_FORBIDDEN_TITLE = '无权限：创建 / 复制 / 暂停·恢复运营仅 root / admin 可操作';

/** 删除 App 菜单项禁用说明（无契约端点，记账见计划 §五） */
const DELETE_APP_DISABLED_TITLE =
  '删除 App 无契约端点（dev-docs ADM-APP-S001~S011 无删除需求 · server 无 DELETE /apps/:id）· 见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五';

/**
 * 暂停 / 恢复运营 mutation — POST /apps/:id/disable|enable。
 * 成功按服务端返回真值 toast + invalidate ['apps']（列表 + 详情同前缀）；
 * 失败回调 onError 供 Overview Switch bump nonce 回滚视觉态（不假成功）。
 */
function useSetAppActive(onError?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? enableApp(id) : disableApp(id),
    // async + await invalidate：isPending 覆盖到 refetch 落地，关掉「响应已回、列表未刷、
    // Switch 复活」窗口（同 system-config 接真口径）
    onSuccess: async (dto) => {
      if (dto.is_active) toast(`已恢复 ${dto.name} 运营`, { tone: 'pos', icon: 'IconCheck' });
      else
        toast(`已暂停运营 ${dto.name} · 客户端请求将被拒绝`, { tone: 'warn', icon: 'IconPause' });
      await queryClient.invalidateQueries({ queryKey: appQueryKeys.all });
    },
    // 失败 ≠ 一定没写入（响应丢失 / 超时同走此路）：回滚 Switch 视觉态后 refetch 对账服务端真值，
    // 不盲信旧状态（同 system-config 接真口径）
    onError: async (err, vars) => {
      onError?.();
      toast(`${vars.active ? '恢复' : '暂停'}运营失败 · ${errMsg(err)}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      await queryClient.invalidateQueries({ queryKey: appQueryKeys.all });
    },
  });
}

/**
 * App 生命周期二次确认配置工厂（复刻设计稿 S8-2）。
 * Why：暂停 / 恢复运营的确认文案此前在 list 菜单 / card 菜单 / DetailDanger 三处各写一遍，
 * 靠人肉保持一致极易漂移——抽成单一工厂，三处共用，根除文案漂移。
 * - pause：tone warn + IconPause + requireTyping（输 App 名）二次确认，文案强调维护态 / 权益保留
 * - resume：tone default + IconPlay，无 requireTyping，文案说明退出维护态、请求重新被受理
 */
function buildAppLifecycleConfirm(
  app: Pick<AppRow, 'name'>,
  action: 'pause' | 'resume',
  onConfirm: () => void,
): ConfirmConfig {
  if (action === 'pause') {
    return {
      title: `暂停运营 ${app.name}？`,
      message:
        '暂停后客户端所有请求将被服务端拒绝，App 进入维护态。已购权益与历史数据保留，恢复运营即可继续使用。',
      tone: 'warn',
      icon: 'IconPause',
      requireTyping: app.name,
      confirmLabel: '暂停运营',
      onConfirm,
    };
  }
  return {
    title: `恢复运营 ${app.name}？`,
    message: '恢复后客户端请求将重新被服务端受理，App 退出维护态。',
    tone: 'default',
    icon: 'IconPlay',
    confirmLabel: '恢复运营',
    onConfirm,
  };
}

/**
 * 导出 App 配置 JSON — dev-docs 未定义导出端点（计划 §五）：
 * GET /apps/:id 取真 AppResponse 后纯前端生成 JSON 下载（真数据导出，非假成功）。
 */
async function exportAppConfigJson(app: Pick<AppRow, 'id' | 'name'>): Promise<void> {
  try {
    const dto = await getApp(app.id);
    const blob = new Blob([JSON.stringify(dto, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${dto.slug || dto.id}-config.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast(`已导出 ${dto.name} 配置 JSON`, { tone: 'pos', icon: 'IconDownload' });
  } catch (err) {
    toast(`导出失败 · ${errMsg(err)}`, { tone: 'neg', icon: 'IconAlert' });
  }
}

// ---------- 详情可编辑表单（契约字段 ↔ tab 控件）----------
/**
 * 详情页受控表单 — UpdateAppRequest 可改字段子集（slug / 平台标识创建后不可改不入表单）：
 *   name / regionCodes(region_codes) / allowedLanguages(allowed_languages) /
 *   bonusRatioPct(third_party_bonus_ratio，百分比显示值) / pricePerKw(default_chapter_price_per_kw) /
 *   taskRewards(task_center_rewards) / linkSchema(app_link_scheme，ADM-APP-S011 必填、不可空) /
 *   readingPass*(小时阅读卡三参，ADM-APP-S014)
 */
interface AppConfigForm {
  name: string;
  /** ADM-APP-S019 包体代号输入框原值（输入中不改写，失焦时 trim + 转大写）；空串 = 未配置，提交时转 `null` */
  appCode: string;
  /**
   * 打开页面时读到的代号（`dto.app_code`）。**只有与它不同才提交**（同 taskDisplayTouched /
   * homeScreenConfigTouched）：PATCH「不传即不改」，无条件带上这份旧值的话，别的管理员在此期间改过的
   * 代号会被随手一次无关保存（如只改 App 名）静默改回去。
   */
  appCodeLoaded: string | null;
  /** 本次 DTO 是否带了 `app_code`（同 storeReviewVersionSupported：Web 先于 Server 部署的窗口）*/
  appCodeSupported: boolean;
  /** 行内错误：前端格式预校验未过，或服务端 RESOURCE_CONFLICT / VALIDATION_FAILED 点名 app_code */
  appCodeError: string | null;
  regionCodes: string[];
  allowedLanguages: string[];
  /** 默认内容语言（ADM-APP-S012，单选、∈ allowedLanguages） */
  defaultLanguage: string;
  bonusRatioPct: string;
  pricePerKw: string;
  taskRewards: TaskCenterRewards;
  rewardedAdConfig: RewardedAdConfigDraft;
  rewardedAdConfigTouched: boolean;
  rewardedAdConfigSupported: boolean;
  /** ADM-APP-S016：任务中心普通任务的开放与排序；**数组顺序即展示顺序**，不存排序整数 */
  taskDisplay: TaskCenterTaskDisplayEntry[];
  /**
   * 本次 DTO 是否**带了** `task_center_task_display`。同 readingPassSupported：Web 先于 Server
   * 发布的窗口内旧 Worker 不返它，而其 `UpdateAppRequestSchema` 是 `.strict()` —— 这时若照常
   * 把该键塞进 PATCH，会被判未知键 `VALIDATION_FAILED`，**整个 App 配置都存不了**。
   * 故窗口内整体省略该键，其余配置照常可改。
   */
  taskDisplaySupported: boolean;
  /**
   * 库里存的值**不合 contract**（手工 SQL 改坏等）。此时渲染用的是 `parseTaskCenterTaskDisplay`
   * 回落的默认值（8 项全开），**不是**运营配过的东西。
   *
   * 🔴 **坏数据时默认不提交**：C 端对不合 contract 的配置是 fail-closed —— 未出现在数组里的 slug
   * 按**关闭**处理。若把回落的「全开」默认值随任何一次无关保存（比如只改个 App 名）写回去，
   * 就**静默重开了运营已关闭的任务**，而且没人会注意到。故坏数据时该键整体省略，
   * 直到运营在本页显式改动 / 点「按上方配置修复」为止。
   */
  taskDisplayMalformed: boolean;
  /** 运营本次是否**主动**改过该配置（含显式点修复）；坏数据时它是提交的唯一放行条件 */
  taskDisplayTouched: boolean;
  linkSchema: string;
  homeScreenConfig: HomeScreenConfig | null;
  homeScreenConfigTouched: boolean;
  /** 小时阅读卡 App 级总开关（ADM-APP-S014）；PATCH 是唯一切换入口 */
  readingPassEnabled: boolean;
  /**
   * 免费体验卡时长（秒）/ 开启天数 = 功能解锁所需的注册自然日龄（ADM-APP-S014）。
   * 存字符串而非 number：受控 input 中间态（清空、输入中）不能塞回 number，
   * 否则「删到空」会被迫变成 0 并即时撞下界。提交时才转 number 交给 Zod 判 `> 0` / `>= 1`。
   */
  readingPassTrialSeconds: string;
  readingPassUnlockDays: string;
  /**
   * 本次 DTO 是否**带了**这三个字段。Web 先于 Server 发布的窗口内旧 Worker 不返它们，而它的
   * `UpdateAppRequestSchema` 是 `.strict()` —— 这时若照常把三个键塞进 PATCH，会被判未知键
   * `VALIDATION_FAILED`，**整个 App 配置仍然存不了**（光在前端兜底渲染值救不了保存）。
   * 故窗口内整体省略这三项：其余配置照常可改，阅读卡参数等 Server 跟上后再配。
   */
  readingPassSupported: boolean;
  readingPassReminderEnabled: boolean;
  readingPassReminderTime: string;
  readingPassReminderMaxCount: string;
  readingPassReminderSupported: boolean;
  /** PATCH 逐字段提交本次编辑，避免无关保存覆盖其他运营人员的提醒配置。 */
  readingPassReminderEnabledTouched: boolean;
  readingPassReminderTimeTouched: boolean;
  readingPassReminderMaxCountTouched: boolean;
  novelPurchaseEnabled: boolean;
  novelPurchaseUnlockDays: string;
  novelPurchaseProducts: Record<'L1' | 'L2' | 'L3' | 'L4' | 'L5', string>;
  novelPurchaseSupported: boolean;
  /** ADM-APP-S017 审核版本号；空串 = 关闭（提交时转成 `null`，**不发空串**——契约把空串判 VALIDATION_FAILED）*/
  storeReviewVersion: string;
  /** 本次 DTO 是否带了 `store_review_version`（同 readingPassSupported：Web 先于 Server 部署的窗口）*/
  storeReviewVersionSupported: boolean;
}

/** AppResponse → 表单初值（真值初始化；#413 起 en 为普通候选，不再防御性注入） */
function formFromDto(dto: AppResponse): AppConfigForm {
  return {
    name: dto.name,
    appCode: dto.app_code ?? '',
    appCodeLoaded: dto.app_code ?? null,
    // 判「键在不在」：旧 Worker 不返该键，其 `.strict()` PATCH schema 也不认它 —— 窗口内照常塞进 PATCH
    // 会让**整次保存** VALIDATION_FAILED，故不支持时整体省略、输入框置灰
    appCodeSupported: 'app_code' in (dto as Record<string, unknown>),
    appCodeError: null,
    regionCodes: [...dto.region_codes],
    allowedLanguages: [...dto.allowed_languages],
    // ADM-APP-S012：默认内容语言初值取真值（NOT NULL，∈ allowed_languages）
    defaultLanguage: dto.default_language,
    bonusRatioPct: ratioToPct(dto.third_party_bonus_ratio),
    pricePerKw: dto.default_chapter_price_per_kw,
    taskRewards: parseTaskCenterRewards(dto.task_center_rewards),
    rewardedAdConfig: rewardedAdConfigToDraft(dto.rewarded_ad_config ?? DEFAULT_REWARDED_AD_CONFIG),
    rewardedAdConfigTouched: false,
    rewardedAdConfigSupported: dto.rewarded_ad_config !== undefined,
    // ADM-APP-S016：宽 jsonb 解析失败回契约默认值（不把坏数据渲染给运营编辑 —— 那会让他一保存
    // 就把坏数据原样写回去）。缺字段（旧 Worker）时同样回默认值，仅作渲染；是否提交看 supported。
    taskDisplay: parseTaskCenterTaskDisplay(dto.task_center_task_display),
    // 新版配置为八项；仅有旧七项字段不代表 Worker 已支持 watch_rewarded_ad。
    taskDisplaySupported:
      dto.rewarded_ad_config !== undefined &&
      'task_center_task_display' in (dto as Record<string, unknown>),
    // 「带了这个键、但解析不出合法配置」才算坏数据；旧 Worker 压根不返该键的情况归 supported 判。
    taskDisplayMalformed:
      dto.rewarded_ad_config !== undefined &&
      'task_center_task_display' in (dto as Record<string, unknown>) &&
      !TaskCenterTaskDisplaySchema.safeParse(dto.task_center_task_display).success,
    taskDisplayTouched: false,
    // WEB scheme 为 NULL，表单字符串使用空值展示。
    linkSchema: dto.app_link_scheme ?? '',
    homeScreenConfig: HomeScreenConfigSchema.safeParse(dto.home_screen_config).success
      ? (dto.home_screen_config as HomeScreenConfig)
      : null,
    homeScreenConfigTouched: false,
    // ADM-APP-S014：三参在契约上均 NOT NULL 必返，但**部署窗口内会缺**——本仓的 staging / prod
    // 发布顺序是 Web 先于 Server（`.claude/commands/deploy.md`），旧 Worker 不返这三个字段。
    // 不兜底的话 `String(undefined)` = `"undefined"`，过不了下面的整数校验 ⇒ **整个 App 配置页
    // 保存不了**（不只是阅读卡这几项），且开关显示成「已关闭」；而窗口是每次发布必然出现、
    // 不是假设。故按契约缺省兜底：真值缺失的代价被限制在「这一个开关短暂显示为默认态」。
    // 只对 `undefined`（= 字段不存在）生效；后端返了错值仍会原样透出，不被掩盖。
    readingPassEnabled: dto.is_reading_pass_enabled ?? true,
    readingPassTrialSeconds: String(dto.reading_pass_free_trial_duration_seconds ?? 1800),
    readingPassUnlockDays: String(dto.reading_pass_unlock_reading_day_threshold ?? 2),
    // 判「键在不在」而不是「值是不是 undefined」：后者分不清「旧 Worker 没这字段」与
    // 「新 Worker 返了 undefined」，而前者正是部署窗口的判据。
    readingPassSupported: 'is_reading_pass_enabled' in (dto as Record<string, unknown>),
    readingPassReminderEnabled: dto.is_reading_pass_free_trial_remind_enabled ?? false,
    readingPassReminderTime: dto.reading_pass_free_trial_remind_time ?? '12:00',
    readingPassReminderMaxCount: String(dto.reading_pass_free_trial_remind_max_count ?? 3),
    readingPassReminderEnabledTouched: false,
    readingPassReminderTimeTouched: false,
    readingPassReminderMaxCountTouched: false,
    // 提醒与语言 CRUD 同版部署；缺任一新字段时不 PATCH 未知键，也不请求新端点。
    readingPassReminderSupported: [
      'is_reading_pass_free_trial_remind_enabled',
      'reading_pass_free_trial_remind_time',
      'reading_pass_free_trial_remind_max_count',
    ].every((field) => field in dto),
    novelPurchaseEnabled: dto.is_novel_purchase_enabled ?? false,
    novelPurchaseUnlockDays: String(dto.novel_purchase_unlock_days ?? 7),
    novelPurchaseProducts: Object.fromEntries(
      (['L1', 'L2', 'L3', 'L4', 'L5'] as const).map((level) => [
        level,
        dto.novel_purchase_products?.find((row) => row.price_level === level)?.access_level_id ??
          '',
      ]),
    ) as Record<'L1' | 'L2' | 'L3' | 'L4' | 'L5', string>,
    novelPurchaseSupported: 'is_novel_purchase_enabled' in (dto as Record<string, unknown>),
    storeReviewVersion: dto.store_review_version ?? '',
    storeReviewVersionSupported: 'store_review_version' in (dto as Record<string, unknown>),
  };
}

/** 将支持的平台筛选映射为 canonical apps 列表查询参数。 */
export function toAppPlatformQuery(selected: string[]): AppPlatform | undefined {
  if (selected.length !== 1) return undefined;
  if (selected[0] === 'iOS') return 'IOS';
  if (selected[0] === 'Android') return 'ANDROID';
  if (selected[0] === 'WEB') return 'WEB';
  return undefined;
}

export default function AppsPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid'); // grid | list
  const [q, setQ] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [dialog, setDialog] = useState<DialogState>(null);
  const platformQuery = toAppPlatformQuery(platforms);
  const {
    data: apps,
    isLoading,
    isError: appsLoadFailed,
    error: appsLoadError,
    refetch: refetchApps,
  } = useApps(platformQuery);
  const closeDialog = () => setDialog(null);
  // 创建 / 复制 / 生命周期 = root / admin（与 server requireRole 一致）；viewer·operator 写控件 disabled
  const canManage = useCanManageApp();

  const setActiveMutation = useSetAppActive();
  const setAppActive = (a: AppRow, active: boolean) =>
    setActiveMutation.mutate({ id: a.id, active });

  if (selected) {
    const app = apps.find((a) => a.id === selected);
    if (app)
      return (
        <>
          <AppDetail
            app={app}
            onBack={() => setSelected(null)}
            setDialog={setDialog}
            onSetActive={setAppActive}
          />
          {renderDialog(
            dialog,
            closeDialog,
            setSelected,
            apps.some((app) => app.rewardedAdConfig !== undefined),
          )}
        </>
      );
  }

  // 列表数据未就绪不再整页 gate：页头（标题 / 视图切换 / 新建 App）+ 筛选行始终渲染（各用自身 state），
  // 仅卡片 / 表格数据区在 useApps 加载期内置骨架——避免一条慢/挂的列表查询把整页 chrome 连同操作入口一起锁死
  const allMarkets = [...new Set(apps.flatMap((a) => a.market))].sort();
  const platformsPresent = (['iOS', 'Android', 'WEB'] as const).filter((p) =>
    apps.some((a) => a.platform === p),
  );
  const filtered = apps.filter((a) => {
    if (platforms.length > 0 && !platforms.includes(a.platform)) return false;
    // 关键字命中名称 / ID / 包体代号（ADM-APP-S019，与服务端 q 同为不区分大小写的子串匹配）
    if (
      q &&
      !(
        a.name.toLowerCase().includes(q.toLowerCase()) ||
        a.id.includes(q.toLowerCase()) ||
        (a.appCode ?? '').toLowerCase().includes(q.toLowerCase())
      )
    )
      return false;
    if (markets.length && !a.market.some((m) => markets.includes(m))) return false;
    if (statuses.length && !statuses.includes(a.status)) return false;
    return true;
  });

  return (
    <div className="page page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">App 管理</h1>
          <div className="page-sub" style={appsLoadFailed ? { color: 'var(--neg)' } : undefined}>
            {appsLoadFailed ? (
              'App 列表加载失败 · 请重试'
            ) : (
              <>
                {apps.length} 个矩阵 App · 覆盖 {allMarkets.length} 个地区
                {platformsPresent.length ? ` · ${platformsPresent.join(' · ')}` : ''}
              </>
            )}
          </div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          <div className="seg">
            <button
              type="button"
              className={view === 'grid' ? 'on' : ''}
              aria-label="卡片视图"
              onClick={() => setView('grid')}
            >
              <Icons.IconLayout size={11} />
            </button>
            <button
              type="button"
              className={view === 'list' ? 'on' : ''}
              aria-label="列表视图"
              onClick={() => setView('list')}
            >
              <Icons.IconList size={11} />
            </button>
          </div>
          <button
            className="btn btn-accent"
            disabled={!canManage}
            title={canManage ? undefined : MANAGE_FORBIDDEN_TITLE}
            onClick={() => setDialog({ kind: 'new-app' })}
          >
            <Icons.IconPlus size={13} />
            新建 App
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="hstack" style={{ gap: 8, padding: '0 0 4px' }}>
        <SearchInput
          width={240}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索 App 名称、ID、包体代号"
        />
        <FilterButton
          label="平台"
          value={platforms}
          onChange={setPlatforms}
          options={[
            { value: 'iOS', label: 'iOS' },
            { value: 'Android', label: 'Android' },
            { value: 'WEB', label: 'WEB (H5)' },
          ]}
          width={160}
        />
        <FilterButton
          label="地区"
          value={markets}
          onChange={setMarkets}
          options={allMarkets.map((m) => ({ value: m, label: fmtCountry(m) }))}
          searchable
          width={220}
        />
        <FilterButton
          label="状态"
          value={statuses}
          onChange={setStatuses}
          options={[
            { value: 'active', label: '● 运营中' },
            { value: 'paused', label: '● 已暂停' },
          ]}
          width={160}
        />
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          {appsLoadFailed ? '列表暂不可用' : `共 ${filtered.length} / ${apps.length} 项`}
        </span>
      </div>

      {/* 数据区：加载期显骨架、失败显可重试诊断，成功才渲真实 grid / table；
          页头 + 筛选行始终保留，慢/挂/rollout 歧义都不整页 gate，也不把失败伪装成 0 个 App。 */}
      {isLoading ? (
        <CardGridSkeleton count={9} />
      ) : appsLoadFailed ? (
        <div
          className="card"
          role="alert"
          aria-live="assertive"
          style={{
            minHeight: 240,
            padding: 40,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            textAlign: 'center',
          }}
        >
          <Icons.IconAlert size={28} style={{ color: 'var(--neg)' }} />
          <div style={{ fontSize: 14, fontWeight: 500 }}>App 列表加载失败</div>
          <div style={{ maxWidth: 520, fontSize: 12, color: 'var(--fg-muted)' }}>
            {appsLoadError?.message ?? '未能从服务端获取 App 列表，请重试。'}
          </div>
          <button type="button" className="btn" onClick={refetchApps}>
            <Icons.IconRefresh size={12} />
            重试
          </button>
        </div>
      ) : view === 'grid' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}
        >
          {filtered.map((a) => (
            <AppCard
              key={a.id}
              app={a}
              canManage={canManage}
              onClick={() => setSelected(a.id)}
              setDialog={setDialog}
              onSetActive={setAppActive}
            />
          ))}
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <Table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox" />
                </th>
                <th>App</th>
                <th>包体代号</th>
                <th>平台</th>
                <th>地区</th>
                <th>允许语言集合</th>
                <th className="num">书籍</th>
                <th className="num">DAU</th>
                <th className="num">本月收入</th>
                <th>状态</th>
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(a.id)}>
                  <td>
                    <input type="checkbox" onClick={(e) => e.stopPropagation()} />
                  </td>
                  <td>
                    <div className="hstack" style={{ gap: 10 }}>
                      <AppIcon app={a} size="sm" />
                      <div>
                        <div style={{ fontWeight: 500 }}>{a.name}</div>
                        <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                          {a.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {a.appCode ?? '—'}
                  </td>
                  <td>
                    <span className="chip">{a.platform}</span>
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {a.market
                      .slice(0, 3)
                      .map((m) => fmtCountry(m))
                      .join(' · ')}
                    {a.market.length > 3 ? ' …' : ''}
                  </td>
                  <td>
                    <span className="mono" style={{ fontSize: 11 }}>
                      {a.lang.length} 种
                    </span>
                    {a.lang.includes('en') && (
                      <span
                        className="mono"
                        style={{ marginLeft: 6, fontSize: 10, color: 'var(--fg-faint)' }}
                      >
                        · 含 en
                      </span>
                    )}
                  </td>
                  <td className="num">{a.books?.toLocaleString() ?? '—'}</td>
                  <td className="num">{a.dau?.toLocaleString() ?? '—'}</td>
                  <td className="num">
                    {a.revenue != null ? `€${a.revenue.toLocaleString()}` : '—'}
                  </td>
                  <td>
                    <span className={`chip ${a.status === 'active' ? 'chip-pos' : 'chip-warn'}`}>
                      {a.status === 'active' ? '运营中' : '已暂停'}
                    </span>
                  </td>
                  <td>
                    <ContextMenu
                      items={[
                        {
                          label: '查看详情',
                          icon: 'IconEye',
                          onClick: () => setSelected(a.id),
                        },
                        {
                          label: '复制 App',
                          icon: 'IconCopy',
                          disabled: !canManage,
                          title: canManage ? undefined : MANAGE_FORBIDDEN_TITLE,
                          onClick: () => setDialog({ kind: 'copy-app', source: a }),
                        },
                        '---',
                        {
                          label: a.status === 'active' ? '暂停运营' : '恢复运营',
                          icon: a.status === 'active' ? 'IconPause' : 'IconPlay',
                          disabled: !canManage,
                          title: canManage ? undefined : MANAGE_FORBIDDEN_TITLE,
                          onClick: () =>
                            setDialog({
                              kind: 'confirm',
                              config:
                                a.status === 'active'
                                  ? buildAppLifecycleConfirm(a, 'pause', () =>
                                      setAppActive(a, false),
                                    )
                                  : buildAppLifecycleConfirm(a, 'resume', () =>
                                      setAppActive(a, true),
                                    ),
                            }),
                        },
                        {
                          label: '导出 JSON',
                          icon: 'IconDownload',
                          onClick: () => void exportAppConfigJson(a),
                        },
                        '---',
                        {
                          label: '删除 App',
                          icon: 'IconTrash',
                          tone: 'danger',
                          disabled: true,
                          title: DELETE_APP_DISABLED_TITLE,
                        },
                      ]}
                    >
                      <button
                        className="btn btn-ghost btn-icon"
                        style={{ width: 24, height: 24 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Icons.IconMoreV size={13} />
                      </button>
                    </ContextMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {renderDialog(
        dialog,
        closeDialog,
        setSelected,
        apps.some((app) => app.rewardedAdConfig !== undefined),
      )}
    </div>
  );
}

/** 渲染当前打开的弹窗（new-app / copy-app / confirm）；onSelect 供确认后跳转用（此处未用） */
function renderDialog(
  dialog: DialogState,
  onClose: () => void,
  _onSelect: (id: string | null) => void,
  rewardedAdSupported: boolean,
): ReactNode {
  if (!dialog) return null;
  if (dialog.kind === 'new-app')
    return <NewAppWizard onClose={onClose} rewardedAdSupported={rewardedAdSupported} />;
  if (dialog.kind === 'copy-app') return <CopyAppDialog source={dialog.source} onClose={onClose} />;
  const c = dialog.config;
  return (
    <ConfirmDialog
      title={c.title}
      message={c.message}
      tone={c.tone}
      icon={c.icon}
      confirmLabel={c.confirmLabel}
      cancelLabel={c.cancelLabel}
      requireTyping={c.requireTyping}
      onConfirm={() => c.onConfirm?.()}
      onClose={onClose}
    />
  );
}

/** 弹窗派发器类型：页面级 setDialog，供卡片 / 详情子组件触达本页弹窗 */
type SetDialog = (d: DialogState) => void;
/** 暂停 / 恢复运营派发器：页面级 mutation 包装，供卡片 / 详情子组件复用 */
type SetActive = (app: AppRow, active: boolean) => void;

function AppCard({
  app,
  canManage,
  onClick,
  setDialog,
  onSetActive,
}: {
  app: AppRow;
  canManage: boolean;
  onClick: () => void;
  setDialog: SetDialog;
  onSetActive: SetActive;
}) {
  return (
    <div
      className="card"
      onClick={onClick}
      style={{
        cursor: 'pointer',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        transition: 'border-color 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-strong)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
        <AppIcon app={app} size="md" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="hstack" style={{ gap: 8 }}>
            <div style={{ fontWeight: 500, fontSize: 14 }}>{app.name}</div>
            <span
              className={`chip ${app.status === 'active' ? 'chip-pos' : 'chip-warn'}`}
              style={{ fontSize: 10 }}
            >
              {app.status === 'active' ? '● 运营中' : '● 已暂停'}
            </span>
          </div>
          <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
            {app.id} · {app.platform}
            {app.appCode ? ` · ${app.appCode}` : ''}
          </div>
        </div>
        <ContextMenu
          items={[
            { label: '查看详情', icon: 'IconEye', onClick: () => onClick() },
            {
              label: '复制 App',
              icon: 'IconCopy',
              disabled: !canManage,
              title: canManage ? undefined : MANAGE_FORBIDDEN_TITLE,
              onClick: () => setDialog({ kind: 'copy-app', source: app }),
            },
            '---',
            {
              label: app.status === 'active' ? '暂停运营' : '恢复运营',
              icon: app.status === 'active' ? 'IconPause' : 'IconPlay',
              disabled: !canManage,
              title: canManage ? undefined : MANAGE_FORBIDDEN_TITLE,
              onClick: () =>
                setDialog({
                  kind: 'confirm',
                  config:
                    app.status === 'active'
                      ? buildAppLifecycleConfirm(app, 'pause', () => onSetActive(app, false))
                      : buildAppLifecycleConfirm(app, 'resume', () => onSetActive(app, true)),
                }),
            },
            {
              label: '导出 JSON',
              icon: 'IconDownload',
              onClick: () => void exportAppConfigJson(app),
            },
            '---',
            {
              label: '删除 App',
              icon: 'IconTrash',
              tone: 'danger',
              disabled: true,
              title: DELETE_APP_DISABLED_TITLE,
            },
          ]}
        >
          <button
            className="btn btn-ghost btn-icon"
            style={{ width: 24, height: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Icons.IconMoreV size={13} />
          </button>
        </ContextMenu>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 6,
          paddingTop: 8,
          borderTop: '1px dashed var(--border)',
        }}
      >
        <Mini label="DAU" value={app.dau ?? '—'} />
        <Mini label="书籍" value={app.books ?? '—'} />
        <Mini
          label="本月"
          value={app.revenue != null ? `€${(app.revenue / 1000).toFixed(1)}k` : '—'}
        />
      </div>

      <div
        className="hstack"
        style={{ gap: 4, flexWrap: 'wrap', paddingTop: 6, borderTop: '1px dashed var(--border)' }}
      >
        {app.market.map((m) => (
          <span key={m} className="chip" style={{ fontSize: 10 }}>
            <Icons.IconGlobe size={10} />
            {fmtCountry(m)}
          </span>
        ))}
        <span className="chip" style={{ fontSize: 10 }}>
          <Icons.IconLanguages size={10} />
          {app.lang.length} 种语言
        </span>
      </div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 14, fontWeight: 600 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div style={{ fontSize: 10, color: 'var(--fg-faint)' }}>{label}</div>
    </div>
  );
}

// ---------- Detail ----------
function AppDetail({
  app,
  onBack,
  setDialog,
  onSetActive,
}: {
  app: AppRow;
  onBack: () => void;
  setDialog: SetDialog;
  onSetActive: SetActive;
}) {
  const [tab, setTab] = useState('overview');
  const [dirty, setDirty] = useState(false);
  const [rewardedAdErrors, setRewardedAdErrors] = useState<RewardedAdFieldErrors>({});
  const [readingPassSaveError, setReadingPassSaveError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // 配置编辑（PATCH /apps/:id）和复制 / 生命周期均为 root/admin（与 server requireRole 一致）。
  const canEditConfig = useCanEditAppConfig();
  const canManage = useCanManageApp();
  // 详情真值：GET /apps/:id 原始契约 DTO（5 tab 受控表单按契约字段初始化）
  const { data: dto, isLoading: detailLoading } = useAppDetail(app.id);
  const [form, setForm] = useState<AppConfigForm | null>(null);
  // dto 到达且无未保存编辑时镜像服务端真值（保存成功 invalidate / 放弃更改 后自动回种）
  useEffect(() => {
    if (dto && !dirty) setForm(formFromDto(dto));
  }, [dto, dirty]);
  const update = (p: Partial<AppConfigForm>) => setForm((f) => (f ? { ...f, ...p } : f));

  const tabs = (
    [
      { id: 'overview', label: '概览', icon: 'IconChart' },
      { id: 'market', label: '地区 & 语言', icon: 'IconGlobe' },
      { id: 'pricing', label: '计价 & BONUS', icon: 'IconCoin' },
      { id: 'payment', label: '支付渠道', icon: 'IconWallet' },
      { id: 'rewarded', label: '激励广告', icon: 'IconStar' },
      { id: 'tasks', label: '任务奖励', icon: 'IconStar' },
      { id: 'link', label: 'Link Schema', icon: 'IconExternal' },
      { id: 'danger', label: '高级操作', icon: 'IconShield' },
    ] as const
  ).filter((item) => app.platform !== 'WEB' || !['tasks', 'link', 'rewarded'].includes(item.id));

  // 保存配置 → PATCH /apps/:id（UpdateAppRequest；slug / 平台标识创建后不可改不提交）
  const saveMutation = useMutation({
    mutationFn: (body: UpdateAppRequest) => updateApp(app.id, body),
    // 先清 dirty 再 await invalidate（重拉详情 / 列表），让 useEffect 回种服务端真值
    onSuccess: async (saved) => {
      setDirty(false);
      setRewardedAdErrors({});
      setReadingPassSaveError(null);
      toast(`已保存 ${saved.name} 配置`, { tone: 'pos', icon: 'IconCheck' });
      await queryClient.invalidateQueries({ queryKey: appQueryKeys.all });
    },
    // 失败保留 dirty 让运营可重试 / 改正；refetch 对账服务端真值（写可能已部分落地）
    onError: async (err) => {
      // ADM-APP-S019：撞别的 App 的代号（契约 RESOURCE_CONFLICT）/ 形态被拒 → 回到概览、在输入框下点名，
      // 通用 toast 只会说「操作冲突，请刷新后重试」，刷新解决不了重复代号
      if (err instanceof AdminApiError) {
        const errors = Object.fromEntries(
          (err.errors ?? [])
            .filter(({ field }) => field.startsWith('rewarded_ad_config'))
            .map(({ field, message }) => [field, message]),
        );
        if (Object.keys(errors).length > 0) {
          setRewardedAdErrors(errors);
          setTab('rewarded');
        }
      }
      const appCodeIssue = appCodeServerIssue(err);
      setReadingPassSaveError(formatReadingPassError(err));
      if (
        err &&
        typeof err === 'object' &&
        (('errors' in err &&
          Array.isArray(err.errors) &&
          err.errors.some((issue: { field?: string }) => issue.field?.includes('reading_pass'))) ||
          ('code' in err && typeof err.code === 'string' && err.code.includes('READING_PASS')))
      )
        setTab('pricing');
      if (appCodeIssue) {
        update({ appCodeError: appCodeIssue });
        setTab('overview');
      }
      toast(`保存失败 · ${appCodeIssue ?? errMsg(err)}`, { tone: 'neg', icon: 'IconAlert' });
      await queryClient.invalidateQueries({ queryKey: appQueryKeys.all });
    },
  });

  const handleSave = () => {
    if (!form) return;
    setReadingPassSaveError(null);
    // ADM-APP-S014 前端校验（契约 AB §4：`> 0` / `>= 1`，两条均有 DB CHECK 背书）。
    // 受控输入是字符串，先转回整数并当场给**可读中文**提示：Zod 对 NaN 只会报 "Expected number,
    // received nan"，撞到 DB CHECK 更是直接 500 —— 两者运营都看不懂，故在这里拦掉。
    const readingPassTrialSeconds = Number(form.readingPassTrialSeconds.trim());
    const readingPassUnlockDays = Number(form.readingPassUnlockDays.trim());
    // 窗口内（DTO 未带三参）不校验也不提交这三项 —— 它们此刻在 Server 侧根本不存在。
    const readingPassIssue = !form.readingPassSupported
      ? null
      : !Number.isInteger(readingPassTrialSeconds) || readingPassTrialSeconds <= 0
        ? '免费体验卡时长必须是大于 0 的整数（秒）'
        : !Number.isInteger(readingPassUnlockDays) || readingPassUnlockDays < 1
          ? '开启天数必须是不小于 1 的整数'
          : null;
    if (readingPassIssue) {
      toast(`校验未通过 · ${readingPassIssue}`, { tone: 'neg', icon: 'IconAlert' });
      return;
    }
    const reminderIssue =
      form.readingPassReminderSupported &&
      (form.readingPassReminderEnabledTouched ||
        form.readingPassReminderTimeTouched ||
        form.readingPassReminderMaxCountTouched)
        ? readingPassReminderIssue({
            enabled: form.readingPassReminderEnabled,
            time: form.readingPassReminderTime,
            maxCount: form.readingPassReminderMaxCount,
          })
        : null;
    if (reminderIssue) {
      setReadingPassSaveError(reminderIssue);
      setTab('pricing');
      toast(`校验未通过 · ${reminderIssue}`, { tone: 'neg', icon: 'IconAlert' });
      return;
    }
    // ADM-APP-S019：归一后与加载值比，没改就不提交、也不校验（见 appCodeLoaded）。
    // 改了才与服务端同一 schema 预校验，不合格式当场在输入框下提示，不发请求
    const appCode = normalizeAppCodeInput(form.appCode);
    const appCodeChanged = form.appCodeSupported && appCode !== form.appCodeLoaded;
    const appCodeIssue = appCodeChanged ? appCodeInputIssue(form.appCode) : null;
    if (appCodeIssue) {
      update({ appCodeError: appCodeIssue });
      setTab('overview');
      toast(`校验未通过 · ${appCodeIssue}`, { tone: 'neg', icon: 'IconAlert' });
      return;
    }
    const parsed = UpdateAppRequestSchema.safeParse({
      name: form.name.trim(),
      // ADM-APP-S019：**只有本次改过才提交**（见 appCodeLoaded）；留空 = 清空代号 → 发 `null`（契约 §4
      // 可置 null），**不发空串**（空串过不了格式校验）。部署窗口内（DTO 未带该键）整体省略，见 appCodeSupported。
      ...(appCodeChanged ? { app_code: appCode } : {}),
      region_codes: form.regionCodes,
      // zh 禁入（DB CHECK ④）双保险；#413 起运营多选直存、服务端不再注入 en
      allowed_languages: form.allowedLanguages.filter((c) => c !== 'zh'),
      // ADM-APP-S012：默认内容语言（须 ∈ allowed_languages，Zod + service 合并校验兜底）
      default_language: form.defaultLanguage,
      third_party_bonus_ratio: pctToRatio(form.bonusRatioPct),
      default_chapter_price_per_kw: form.pricePerKw.trim(),
      task_center_rewards: form.taskRewards,
      ...(form.rewardedAdConfigSupported && form.rewardedAdConfigTouched
        ? { rewarded_ad_config: rewardedAdConfigFromDraft(form.rewardedAdConfig) }
        : {}),
      // ADM-APP-S016（契约 §4）：整数组替换语义。**部署窗口内整体省略**（见 taskDisplaySupported）。
      // ADM-APP-S016：**只有本次真的改过才提交**。PATCH 语义是「不传即不改」，所以无条件带上
      // 这份「打开页面时读到的」数组没有任何好处，却有两个坏处：
      //   ① **并发丢更新** —— 甲打开页面后乙改了排序，甲随手改个 App 名保存，就把乙的配置
      //      覆盖回甲加载时的旧值（还可能把乙刚关掉的任务重新打开），且双方都不会察觉；
      //   ② 库里是坏数据时会把回落的「8 项全开」默认值写回去，静默重开 C 端已 fail-closed 的任务。
      // 触碰过才提交同时挡住这两条，也让「坏数据不写回」不再需要单独的条件分支。
      ...(form.taskDisplaySupported && form.taskDisplayTouched
        ? { task_center_task_display: form.taskDisplay }
        : {}),
      // WEB 不登记 scheme，不能把表单空串回传为原生唤起标识。
      app_link_scheme: dto?.platform === 'WEB' ? null : form.linkSchema.trim(),
      ...(dto?.platform === 'WEB' && form.homeScreenConfigTouched
        ? { home_screen_config: form.homeScreenConfig }
        : {}),
      // ADM-APP-S014：三参随保存整体提交（PATCH 是总开关的唯一切换入口，契约 §4）。
      // **部署窗口内整体省略**：旧 Worker 的 UpdateAppRequestSchema 是 .strict()，多传未知键会让
      // 整次保存被判 VALIDATION_FAILED —— 光在 formFromDto 兜底渲染值救不了保存这一侧。
      ...(form.readingPassSupported
        ? {
            is_reading_pass_enabled: form.readingPassEnabled,
            reading_pass_free_trial_duration_seconds: readingPassTrialSeconds,
            reading_pass_unlock_reading_day_threshold: readingPassUnlockDays,
          }
        : {}),
      ...(form.readingPassReminderSupported && form.readingPassReminderEnabledTouched
        ? { is_reading_pass_free_trial_remind_enabled: form.readingPassReminderEnabled }
        : {}),
      ...(form.readingPassReminderSupported && form.readingPassReminderTimeTouched
        ? { reading_pass_free_trial_remind_time: form.readingPassReminderTime }
        : {}),
      ...(form.readingPassReminderSupported && form.readingPassReminderMaxCountTouched
        ? { reading_pass_free_trial_remind_max_count: Number(form.readingPassReminderMaxCount) }
        : {}),
      ...(form.storeReviewVersionSupported
        ? {
            // 空输入框 = 关闭 → 发 `null`（契约里唯一的收口手段），**绝不发空串**：
            // 空串被契约判 VALIDATION_FAILED，且逐字节等值下真落库会让所有带空
            // `X-App-Version` 的请求命中审核版本模式。
            store_review_version:
              form.storeReviewVersion.trim() === '' ? null : form.storeReviewVersion.trim(),
          }
        : {}),
      ...(form.novelPurchaseSupported
        ? {
            is_novel_purchase_enabled: form.novelPurchaseEnabled,
            novel_purchase_unlock_days: Number(form.novelPurchaseUnlockDays.trim()),
            novel_purchase_products: (['L1', 'L2', 'L3', 'L4', 'L5'] as const)
              .filter((level) => form.novelPurchaseProducts[level].trim() !== '')
              .map((level) => ({
                price_level: level,
                access_level_id: form.novelPurchaseProducts[level].trim(),
              })),
          }
        : {}),
    });
    if (!parsed.success) {
      const errors = Object.fromEntries(
        parsed.error.issues
          .filter((issue) => issue.path[0] === 'rewarded_ad_config')
          .map((issue) => [issue.path.join('.'), issue.message]),
      );
      if (Object.keys(errors).length > 0) {
        setRewardedAdErrors(errors);
        setTab('rewarded');
      }
      const issue = parsed.error.issues[0];
      toast(`校验未通过 · ${issue ? `${issue.path.join('.')}: ${issue.message}` : '请检查表单'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      return;
    }
    saveMutation.mutate(parsed.data);
  };

  const handleBack = () => {
    if (!dirty) {
      onBack();
      return;
    }
    setDialog({
      kind: 'confirm',
      config: {
        title: '有未保存的更改',
        message: `${app.name} 的配置已有改动但尚未保存。离开后未保存的更改将被丢弃。`,
        tone: 'warn',
        icon: 'IconAlert',
        confirmLabel: '丢弃更改并离开',
        cancelLabel: '继续编辑',
        onConfirm: () => onBack(),
      },
    });
  };

  // Any input / select / textarea / checkbox change inside the form marks dirty
  const onFormChange = () => {
    if (!dirty) setDirty(true);
  };

  // 点击卡片 / 行进入详情 → 详情数据 (GET /apps/:id) 返回前先渲染详情骨架（与原型 detailLoading→DetailSkeleton 同）
  // 传 onBack 让骨架的返回按钮在加载期保持可点：detailLoading 时表单未渲染、无 dirty 态，
  // 直接走 onBack（= setSelected(null) 回列表），避免慢/挂的 GET /apps/:id 把运营困在无返回入口的骨架里
  if (detailLoading) return <DetailSkeleton onBack={onBack} />;

  return (
    <div className="page page-enter">
      {/* Back + header */}
      <div className="hstack" style={{ gap: 8, fontSize: 12, color: 'var(--fg-muted)' }}>
        <button className="btn btn-ghost btn-sm" onClick={handleBack}>
          <Icons.IconChevLeft size={12} />
          返回 App 列表
        </button>
        {dirty && (
          <span className="chip chip-warn" style={{ fontSize: 10 }}>
            ● 有未保存的更改
          </span>
        )}
      </div>

      <div className="page-header" style={{ alignItems: 'center' }}>
        <div className="hstack" style={{ gap: 14 }}>
          <AppIcon app={app} size="lg" />
          <div>
            <div className="hstack" style={{ gap: 8 }}>
              <h1 className="page-title">{app.name}</h1>
              <span className={`chip ${app.status === 'active' ? 'chip-pos' : 'chip-warn'}`}>
                {app.status === 'active' ? '● 运营中' : '● 已暂停'}
              </span>
            </div>
            {/* 真值：slug / 创建·更新时间来自 GET /apps/:id（DTO 无 updated_by，不再显示操作者占位） */}
            <div
              className="mono"
              style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 4 }}
            >
              {app.id} · {dto?.slug ?? '—'} · {app.platform} · 创建于{' '}
              {dto ? fmtDate(dto.created_at) : '—'} · 最近更新{' '}
              {dto ? fmtDateTime(dto.updated_at) : '—'}
            </div>
          </div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          {dirty && (
            <button
              className="btn"
              onClick={() => {
                setDirty(false);
                setRewardedAdErrors({});
              }}
            >
              放弃更改
            </button>
          )}
          {dirty && (
            <button
              className="btn btn-primary"
              disabled={saveMutation.isPending || !canEditConfig}
              title={canEditConfig ? undefined : '无权限：保存配置仅 root / admin 可操作'}
              onClick={handleSave}
            >
              <Icons.IconCheck size={13} />
              {saveMutation.isPending ? '保存中…' : '保存'}
            </button>
          )}
        </div>
      </div>

      {/* KPI strip — 书籍数直接来自 required AppResponse.book_count，真实 0 正常展示；users / finance 聚合未注入 → 显 '—'。
          环比 delta 无 per-app 时序端点 → 整块移除（不显恒 ↑0.0% 绿箭头伪指标） */}
      <div className="card" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {[
          {
            label: '本月收入',
            value: app.revenue != null ? `€${app.revenue.toLocaleString()}` : '—',
          },
          { label: 'DAU', value: app.dau?.toLocaleString() ?? '—' },
          { label: 'MAU', value: app.users?.toLocaleString() ?? '—' },
          { label: '书籍', value: app.books?.toLocaleString() ?? '—' },
          { label: '被动退款率', value: app.refundRate != null ? `${app.refundRate}%` : '—' },
        ].map((s, i) => (
          <div
            key={s.label}
            style={{ padding: 14, borderRight: i < 4 ? '1px solid var(--border)' : '0' }}
          >
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ fontSize: 22, marginTop: 4 }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div
        style={{ borderBottom: '1px solid var(--border)', display: 'flex', gap: 0, marginTop: 6 }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className="focusable"
            style={{
              border: 0,
              background: 'transparent',
              padding: '9px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              font: 'inherit',
              fontSize: 12.5,
              color: tab === t.id ? 'var(--fg-strong)' : 'var(--fg-muted)',
              fontWeight: tab === t.id ? 500 : 400,
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {(() => {
              const TabIcon = Icons[t.icon];
              return <TabIcon size={13} />;
            })()} {t.label}
          </button>
        ))}
      </div>

      {/* 各 tab 的卡片（Section / 面板）直接是本容器的子项：统一用 vstack 给卡片间距，
          否则相邻卡片边框贴在一起（支付渠道 tab 的图标 / 策略 / 就绪面板尤其明显）。 */}
      <div className="vstack" style={{ gap: 12 }} onInput={onFormChange} onChange={onFormChange}>
        <AppDirtyCtx.Provider value={onFormChange}>
          {!dto || !form ? (
            <div
              style={{ padding: 28, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}
            >
              配置加载中…
            </div>
          ) : (
            <>
              {tab === 'overview' && (
                <>
                  <DetailOverview
                    app={app}
                    dto={dto}
                    form={form}
                    update={update}
                    canEdit={canEditConfig}
                  />
                  {app.platform === 'WEB' && (
                    <WebHomeScreenSection form={form} update={update} canEdit={canEditConfig} />
                  )}
                </>
              )}
              {tab === 'market' && <DetailMarket form={form} update={update} />}
              {tab === 'pricing' && (
                <>
                  <DetailPricing form={form} update={update} isWeb={app.platform === 'WEB'} />
                  {app.platform !== 'WEB' && (
                    <>
                      {/* 阅读卡沿用原生 App 边界；WEB 独立商品目录不支持阅读卡产品。 */}
                      {readingPassSaveError && (
                        <p role="alert" style={{ color: 'var(--neg)', overflowWrap: 'anywhere' }}>
                          {readingPassSaveError}
                        </p>
                      )}
                      <ReadingPassSection
                        form={form}
                        update={update}
                        canEdit={canEditConfig && form.readingPassSupported}
                        unsupported={!form.readingPassSupported}
                      />
                      <div
                        onInput={(event) => event.stopPropagation()}
                        onChange={(event) => event.stopPropagation()}
                      >
                        <ReadingPassLanguageConfigsPanel
                          key={dto.id}
                          app={dto}
                          canEdit={canEditConfig}
                          supported={form.readingPassReminderSupported}
                        />
                      </div>
                      <NovelPurchaseSection
                        form={form}
                        update={update}
                        canEdit={canEditConfig && form.novelPurchaseSupported}
                        unsupported={!form.novelPurchaseSupported}
                      />
                      <StoreReviewVersionSection
                        form={form}
                        update={update}
                        canEdit={canEditConfig && form.storeReviewVersionSupported}
                        unsupported={!form.storeReviewVersionSupported}
                      />
                    </>
                  )}
                </>
              )}
              {tab === 'rewarded' && (
                <>
                  <Section
                    title="激励广告 App 默认配置"
                    sub="业务日与注册日龄使用用户固定日切时区；语言覆盖整份优先。任务奖励与限额在准入时快照，章节最终授予使用当前有效上限。"
                  >
                    {!form.rewardedAdConfigSupported && (
                      <p role="status">当前服务端尚未返回激励广告配置，请待发布后刷新。</p>
                    )}
                    <RewardedAdConfigFields
                      value={form.rewardedAdConfig}
                      prefix="rewarded_ad_config"
                      errors={rewardedAdErrors}
                      disabled={!canEditConfig || !form.rewardedAdConfigSupported}
                      onChange={(value) => {
                        update({ rewardedAdConfig: value, rewardedAdConfigTouched: true });
                        setRewardedAdErrors({});
                        onFormChange();
                      }}
                    />
                  </Section>
                  {form.rewardedAdConfigSupported && (
                    <RewardedAdsPanel
                      appId={app.id}
                      platform={dto.platform}
                      allowedLanguages={dto.allowed_languages}
                      defaultConfig={dto.rewarded_ad_config}
                      hasUnsavedAppChanges={dirty || saveMutation.isPending}
                    />
                  )}
                </>
              )}
              {tab === 'payment' && (
                <>
                  {app.platform === 'WEB' ? (
                    <Section
                      title="Web 支付"
                      sub="Web 使用独立商品目录与 Stripe 结算，不使用原生 IAP 或 Adapty placement。"
                    />
                  ) : (
                    <DetailPayment app={app} />
                  )}
                  {/* 支付方式图标（ADM-APP-S013，A8 §7.8.5~§7.8.7）：**排在策略面板之前** ——
                      契约的成员产生路径是「先配图标、再加成员」（§7.8.2 图标前置校验），
                      次序反过来运营会先撞 422 再回头找入口。 */}
                  <div id={PAYMENT_METHOD_ICONS_ANCHOR_ID}>
                    <PaymentMethodIconsPanel appId={app.id} isWeb={app.platform === 'WEB'} />
                  </div>
                  {/* 支付方式策略（ADM-PAY-S008，A8 §7.8）：启停 + 排序 + Region 覆盖的唯一入口 */}
                  <PaymentMethodPoliciesPanel
                    appId={app.id}
                    isWeb={app.platform === 'WEB'}
                    platform={app.platform}
                  />
                  {/* 负向清单：什么在阻止通道被 C 端下发（只列阻断项，不画「会下发」的绿灯）。
                      isActive 必须读同屏已加载的详情 DTO，不能读列表 AppRow.status：打开详情只
                      refetch GET /apps/:id，不 invalidate 列表，两边会分叉。C 端 findAppById 的
                      where 含 is_active=true，停用即全通道不下发，是最高优先级前置。*/}
                  {app.platform !== 'WEB' && (
                    <PaymentChannelReadinessPanel appId={app.id} isActive={dto.is_active} />
                  )}
                </>
              )}
              {tab === 'tasks' && (
                <DetailTasks form={form} update={update} canEdit={canEditConfig} />
              )}
              {tab === 'link' && <DetailLink form={form} update={update} />}
              {tab === 'danger' && (
                <DetailDanger
                  app={app}
                  dto={dto}
                  setDialog={setDialog}
                  onSetActive={onSetActive}
                  canManage={canManage}
                />
              )}
            </>
          )}
        </AppDirtyCtx.Provider>
      </div>
    </div>
  );
}

function DetailOverview({
  app,
  dto,
  form,
  update,
  canEdit,
}: {
  app: AppRow;
  dto: AppResponse;
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
  /** 配置编辑权（root / admin）；包体代号输入按它置灰，保存按钮另有同一闸 */
  canEdit: boolean;
}) {
  const appCodeErrorId = useId();
  // 运营状态在此仅只读展示；启停单一入口在「高级操作」标签（DetailDanger，带 requireTyping 二次确认）
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: 12,
        }}
      >
        <div className="card" style={{ padding: 14 }}>
          <div className="hstack" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontWeight: 500, fontSize: 13.5 }}>收入 & DAU · 30 天</div>
            <div className="hstack" style={{ gap: 6, fontSize: 11, color: 'var(--fg-muted)' }}>
              <span className="hstack" style={{ gap: 4 }}>
                <span
                  style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--chart-1)' }}
                />
                收入
              </span>
              <span className="hstack" style={{ gap: 4 }}>
                <span
                  style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--chart-3)' }}
                />
                DAU
              </span>
            </div>
          </div>
          {/* 去假（对抗审查 round-2 P2）：原渲染 useDashboard 的 dash.revenue30d/dau30d —— 顶栏**全局**
              app-scope 的聚合时序（详情页经 local selected 打开、不改全局 appId），与本详情的具体 App 无关
              → 把全站数据冒充该 App 30 天表现（同 PaywallPage P2-1 模式）。per-app 收入/DAU 30 天序列需
              后端按 app 提供（DAU 另依赖行为埋点），未接入前不展示假图。 */}
          <div
            className="hstack"
            style={{
              fontSize: 11.5,
              color: 'var(--fg-faint)',
              minHeight: 200,
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            单 App 30 天收入 / DAU 趋势需后端按 app 提供，未接入前不展示
          </div>
        </div>
        {/* 支付渠道分布 — 分渠道收入统计端点未就绪 → 占位「待统计」，不编数（计划 §五）；
            渠道闭集 IAP/STRIPE（原型 PayPal 在契约闭集外，不渲染） */}
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontWeight: 500, fontSize: 13.5, marginBottom: 10 }}>支付渠道分布</div>
          <div className="hstack" style={{ gap: 16, alignItems: 'center' }}>
            <Charts.Donut size={110} thickness={14} data={[{ value: 1, color: 'var(--border)' }]} />
            <div className="vstack" style={{ gap: 6, flex: 1 }}>
              {app.platform !== 'WEB' && (
                <Legend dot="var(--chart-1)" label="IAP" val="—" extra="待统计" />
              )}
              <Legend dot="var(--chart-2)" label="Stripe" val="—" extra="待统计" />
            </div>
          </div>
        </div>
      </div>

      <Section title="基础信息" sub="App 平台与稳定 ID 创建后不可修改">
        <FormRow label="App 名称">
          <input
            className="input"
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
            style={{ maxWidth: 360 }}
          />
        </FormRow>
        <FormRow
          label="包体代号"
          hint={
            form.appCodeSupported
              ? `${APP_CODE_FORMAT_HINT} · 全局唯一 · 每个包体（iOS / Android 各一行）各自填写 · 留空 = 未配置 · 用于 AppLovin 创意组名 APP 键，复制 App 不继承`
              : '服务端本次未返回包体代号，暂不可编辑，保存会省略该字段'
          }
        >
          <input
            className="input mono"
            value={form.appCode}
            disabled={!canEdit || !form.appCodeSupported}
            maxLength={12}
            aria-label="包体代号"
            aria-invalid={form.appCodeError !== null}
            aria-describedby={form.appCodeError ? appCodeErrorId : undefined}
            // 输入中保留原值（逐键转大写会挪光标、打断输入法组字），失焦再归一；大写观感靠 text-transform
            onChange={(e) => update({ appCode: e.target.value, appCodeError: null })}
            onBlur={(e) => {
              const normalized = normalizeAppCodeDraft(e.target.value);
              if (normalized !== e.target.value) update({ appCode: normalized });
            }}
            placeholder="如 NVPLI · 留空 = 未配置"
            style={{ maxWidth: 200, textTransform: 'uppercase' }}
          />
          {form.appCodeError && (
            <div
              id={appCodeErrorId}
              role="alert"
              style={{ marginTop: 6, fontSize: 11, color: 'var(--neg)' }}
            >
              {form.appCodeError}
            </div>
          )}
        </FormRow>
        <FormRow label="App ID" hint="稳定标识，创建后不可更改">
          <div className="hstack" style={{ gap: 8 }}>
            <input
              className="input mono"
              defaultValue={app.id}
              disabled
              style={{ maxWidth: 280, background: 'var(--bg-subtle)' }}
            />
            <button
              className="btn btn-sm"
              onClick={() => {
                navigator.clipboard?.writeText(app.id);
                toast(`已复制 ${app.id}`, { tone: 'pos', icon: 'IconCopy' });
              }}
            >
              <Icons.IconCopy size={11} />
              复制
            </button>
          </div>
        </FormRow>
        <FormRow label="平台" hint="iOS / Android / WEB · 创建后不可更改">
          <div className="hstack" style={{ gap: 8 }}>
            <span className="chip">{app.platform}</span>
            {app.platform !== 'WEB' && (
              <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                Bundle ID: {dto.bundle_id_ios ?? dto.package_name_android ?? '—'}
              </span>
            )}
          </div>
        </FormRow>
        <FormRow label="运营状态" hint="单一入口在「高级操作」· 此处仅展示当前状态">
          <div className="hstack" style={{ gap: 10 }}>
            <span className={`chip ${dto.is_active ? 'chip-pos' : 'chip-warn'}`}>
              {dto.is_active ? '● 运营中' : '● 已暂停'}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
              启停操作需二次确认，请前往「高级操作」标签{dto.is_active ? '暂停运营' : '恢复运营'}
            </span>
          </div>
        </FormRow>
      </Section>
    </div>
  );
}

// 产品字典 §0.4：App 配置界面的「已禁用」标记须说明同一组后果；芯片悬停与横幅共用本句，避免两处口径漂移。
const DISABLED_LANGUAGE_CONSEQUENCES =
  '存量仍下发，新内容不再自动翻译，该语言退出运营配置 Tab，无法查看或手工维护';

function DetailMarket({
  form,
  update,
}: {
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
}) {
  const markDirty = useMarkDirty();
  const { data: sys, isSuccess: sysReady } = useSysConfig();
  const markets = form.regionCodes;
  const langs = form.allowedLanguages;
  // 启停判定走 useSysConfig 真数据（不再用 mock seed 的 isRegionEnabled/isLanguageEnabled）
  const regionEnabled = (code: string) =>
    sys.regions.find((r) => r.region_code === code)?.enabled ?? false;
  const langSelectable = (code: string) => {
    const l = sys.languages.find((x) => x.language_code === code);
    return l ? l.enabled && l.app_selectable : false;
  };
  const toggleMarket = (code: string) => {
    update({
      regionCodes: markets.includes(code) ? markets.filter((c) => c !== code) : [...markets, code],
    });
    markDirty();
  };
  const toggleLang = (code: string) => {
    if (code === 'zh') return; // zh 禁入（DB CHECK ④）
    const next = langs.includes(code) ? langs.filter((c) => c !== code) : [...langs, code];
    // 若移除的正是当前默认语言，清空默认语言强制运营显式再选（不按数组位置 [0] 派生——dev-docs §1
    // 「default_language 单选、明确配置、禁用 allowed_languages[0] 推导」，codex #301 P2）
    const nextDefault = next.includes(form.defaultLanguage) ? form.defaultLanguage : '';
    update({ allowedLanguages: next, defaultLanguage: nextDefault });
    markDirty();
  };
  // 2026-05-28 PR#63 候选下拉派生自 system_regions / system_languages
  // 当前启用集 + 存量已选但禁用的（不强制清理，但 UI 标记"已禁用"）
  const visibleRegions = (() => {
    const all = sys.regions;
    const result = all.filter((r) => r.enabled || markets.includes(r.region_code));
    return result.sort((a, b) => {
      const aOn = markets.includes(a.region_code);
      const bOn = markets.includes(b.region_code);
      if (aOn !== bOn) return aOn ? -1 : 1;
      const aEn = a.enabled;
      const bEn = b.enabled;
      if (aEn !== bEn) return aEn ? -1 : 1;
      return a.region_code.localeCompare(b.region_code);
    });
  })();
  const visibleLangs = (() => {
    const all = sys.languages;
    // #413：en 转普通候选（不再排除）；app_selectable=true 才在下拉里 + 存量已选语言（即便后来 disabled）；zh 仍禁入
    const result = all.filter(
      (l) =>
        l.language_code !== 'zh' &&
        ((l.enabled && l.app_selectable) || langs.includes(l.language_code)),
    );
    return result.sort((a, b) => {
      const aOn = langs.includes(a.language_code);
      const bOn = langs.includes(b.language_code);
      if (aOn !== bOn) return aOn ? -1 : 1;
      const aEn = a.enabled;
      const bEn = b.enabled;
      if (aEn !== bEn) return aEn ? -1 : 1;
      return a.language_code.localeCompare(b.language_code);
    });
  })();
  const disabledMarketsSelected = markets.filter((c) => !regionEnabled(c));
  const disabledLangsSelected = sysReady ? langs.filter((c) => !langSelectable(c)) : [];

  return (
    <Section
      title="运营地区 & 允许语言集合"
      sub="候选下拉派生自系统配置 (system_regions / system_languages) · en 为普通候选（#413 起不再自动包含）· 中文禁入"
    >
      <FormRow
        label="运营地区"
        hint={
          <span>
            候选来自 <span className="mono">system_regions WHERE enabled=true</span> ·
            维护入口：系统设置 · 系统配置 · 地区候选
          </span>
        }
      >
        <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
          {visibleRegions.map((m) => {
            const on = markets.includes(m.region_code);
            const isDisabled = !m.enabled;
            return (
              <button
                key={m.region_code}
                type="button"
                className="focusable"
                onClick={() => toggleMarket(m.region_code)}
                title={
                  isDisabled
                    ? '该地区已在系统配置停用 · 仅历史数据保留，建议移除'
                    : (m.display_name_i18n.en as string)
                }
                style={{
                  padding: '5px 10px',
                  borderRadius: 999,
                  border: on
                    ? `1px solid ${isDisabled ? 'var(--warn)' : 'var(--accent)'}`
                    : '1px solid var(--border)',
                  background: on
                    ? isDisabled
                      ? 'var(--warn-soft)'
                      : 'var(--accent-soft)'
                    : 'var(--bg-elev)',
                  color: on
                    ? isDisabled
                      ? 'oklch(0.45 0.12 75)'
                      : 'var(--accent-fg)'
                    : isDisabled
                      ? 'var(--fg-faint)'
                      : 'var(--fg-muted)',
                  font: 'inherit',
                  fontSize: 11.5,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  opacity: isDisabled && !on ? 0.6 : 1,
                }}
              >
                {on && <Icons.IconCheck size={10} />}
                <span className="mono">{m.region_code}</span>
                <span style={{ color: 'var(--fg-faint)' }}>{m.display_name_i18n.zh}</span>
                {isDisabled && (
                  <span className="chip" style={{ fontSize: 9, padding: '0 4px', marginLeft: 2 }}>
                    已禁用
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {disabledMarketsSelected.length > 0 && (
          <div
            className="hstack"
            style={{
              marginTop: 8,
              gap: 6,
              padding: '6px 10px',
              background: 'var(--warn-soft)',
              borderRadius: 5,
            }}
          >
            <Icons.IconAlert size={11} style={{ color: 'var(--warn)' }} />
            <span style={{ fontSize: 11, color: 'var(--fg)' }}>
              存量地区 <span className="mono strong">{disabledMarketsSelected.join(' · ')}</span>{' '}
              已在系统配置停用 · 不强制清理，但建议运营评估
            </span>
          </div>
        )}
      </FormRow>

      <FormRow
        label="允许语言集合"
        hint={
          <span>
            候选来自{' '}
            <span className="mono">
              system_languages WHERE enabled=true AND app_selectable=true
            </span>{' '}
            · 维护入口：系统设置 · 系统配置 · 语言候选
          </span>
        }
      >
        <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
          {visibleLangs.map((l) => {
            const on = langs.includes(l.language_code);
            const isDisabled = !(l.enabled && l.app_selectable);
            return (
              <button
                key={l.language_code}
                type="button"
                className="focusable"
                onClick={() => toggleLang(l.language_code)}
                title={
                  isDisabled
                    ? `该语言已在系统配置停用 · 不强制清理；${DISABLED_LANGUAGE_CONSEQUENCES}`
                    : (l.display_name_i18n.en as string)
                }
                style={{
                  padding: '5px 10px',
                  borderRadius: 999,
                  border: on
                    ? `1px solid ${isDisabled ? 'var(--warn)' : 'var(--accent)'}`
                    : '1px solid var(--border)',
                  background: on
                    ? isDisabled
                      ? 'var(--warn-soft)'
                      : 'var(--accent-soft)'
                    : 'var(--bg-elev)',
                  color: on
                    ? isDisabled
                      ? 'oklch(0.45 0.12 75)'
                      : 'var(--accent-fg)'
                    : isDisabled
                      ? 'var(--fg-faint)'
                      : 'var(--fg-muted)',
                  font: 'inherit',
                  fontSize: 11.5,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  opacity: isDisabled && !on ? 0.6 : 1,
                }}
              >
                {on && <Icons.IconCheck size={10} />}
                <span className="mono">{l.language_code.toUpperCase()}</span>
                <span style={{ color: 'var(--fg-faint)' }}>{l.display_name_i18n.zh}</span>
                {isDisabled && (
                  <span className="chip" style={{ fontSize: 9, padding: '0 4px', marginLeft: 2 }}>
                    已禁用
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div
          className="hstack"
          style={{ marginTop: 8, gap: 6, fontSize: 11, color: 'var(--fg-muted)' }}
        >
          <Icons.IconInfo size={11} style={{ color: 'var(--info)' }} />
          <span>
            已维护 <span className="mono strong">{langs.length}</span> 种 · en 为普通候选（#413
            起不再自动包含）· 请求语言不在集合内 → 服务端 fallback 英语 ·
            <span style={{ marginLeft: 4 }} className="mono">
              zh 禁入（DB CHECK ④）
            </span>
          </span>
        </div>
        {disabledLangsSelected.length > 0 && (
          <div
            className="hstack"
            style={{
              marginTop: 6,
              gap: 6,
              padding: '6px 10px',
              background: 'var(--warn-soft)',
              borderRadius: 5,
            }}
          >
            <Icons.IconAlert size={11} style={{ color: 'var(--warn)' }} />
            <span style={{ fontSize: 11, color: 'var(--fg)' }}>
              存量语言{' '}
              <span className="mono strong">
                {disabledLangsSelected.map((c) => c.toUpperCase()).join(' · ')}
              </span>{' '}
              已在系统配置停用 · 不强制清理；{DISABLED_LANGUAGE_CONSEQUENCES}
            </span>
          </div>
        )}
      </FormRow>

      {/* ADM-APP-S012：默认内容语言 —— 从已选允许语言集合内单选 */}
      <FormRow
        label="默认内容语言"
        hint="客户端首次建立内容语言基线且设备语言 ∉ 允许集合时的回落目标 · 单选 · 须 ∈ 允许语言集合"
      >
        <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
          {langs.length === 0 && (
            <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>请先选择允许语言集合</span>
          )}
          {langs.map((code) => {
            const on = form.defaultLanguage === code;
            // 默认语言取已选集合；编辑保留的停用成员仍可设为默认（产品字典 §0.4）。
            const selectable = sys.languages.some(
              (l) => l.language_code === code && l.app_selectable,
            );
            const disabledLanguage =
              sys.languages.find((l) => l.language_code === code)?.enabled === false;
            return (
              <button
                key={code}
                type="button"
                className="focusable"
                disabled={!selectable}
                title={selectable ? undefined : '该语言不可作为 App 内容语言'}
                onClick={() => {
                  update({ defaultLanguage: code });
                  markDirty();
                }}
                style={{
                  padding: '5px 10px',
                  borderRadius: 999,
                  border: on ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: on ? 'var(--accent-soft)' : 'var(--bg-elev)',
                  color: on ? 'var(--accent-fg)' : 'var(--fg-muted)',
                  font: 'inherit',
                  fontSize: 11.5,
                  cursor: selectable ? 'pointer' : 'not-allowed',
                  opacity: selectable ? 1 : 0.6,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {on && <Icons.IconCheck size={10} />}
                <span className="mono">{code.toUpperCase()}</span>
                {sysReady && (!selectable || disabledLanguage) && (
                  <span className="chip" style={{ fontSize: 9, padding: '0 4px', marginLeft: 2 }}>
                    已禁用
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </FormRow>
    </Section>
  );
}

function DetailPricing({
  form,
  update,
  isWeb = false,
}: {
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
  isWeb?: boolean;
}) {
  const previewCoin = Math.ceil(3200 / 1000) * (Number(form.pricePerKw) || 0);
  return (
    <Section
      title="计价 & BONUS"
      sub="2026-05-28 契约同步 · 千字单价降级为 per-novel 创建时的默认值，实际定价下沉至书籍详情"
    >
      <FormRow
        label="千字单价 (默认值)"
        hint={
          <span>
            仅作为新建 novel 时 <span className="mono">novels.cost_default_per_kw</span> 的初始
            fallback。单本书可在《书籍详情 · 价格 · 千字单价》独立覆盖
          </span>
        }
      >
        <div className="hstack" style={{ gap: 8 }}>
          <input
            className="input mono"
            value={form.pricePerKw}
            onChange={(e) => update({ pricePerKw: e.target.value })}
            style={{ width: 80, textAlign: 'right' }}
          />
          <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>COIN / 千字</span>
          <span className="chip" style={{ marginLeft: 4, fontSize: 10 }}>
            默认值
          </span>
          <div style={{ flex: 1 }} />
          <div
            className="hstack"
            style={{ gap: 8, padding: '5px 10px', background: 'var(--bg-subtle)', borderRadius: 6 }}
          >
            <Icons.IconInfo size={12} style={{ color: 'var(--info)' }} />
            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              3,200 字章节 ≈{' '}
              <span className="mono strong">{previewCoin.toLocaleString()} COIN</span>
            </span>
          </div>
        </div>
        <div
          className="hstack"
          style={{
            marginTop: 10,
            gap: 8,
            padding: '8px 12px',
            border: '1px dashed var(--border)',
            borderRadius: 6,
            background: 'var(--bg-subtle)',
          }}
        >
          <Icons.IconBranch size={12} style={{ color: 'var(--info)' }} />
          <span style={{ fontSize: 11.5, color: 'var(--fg-muted)', flex: 1 }}>
            实际取值优先级：<span className="mono">novels.cost_per_kw</span> →{' '}
            <span className="mono">novels.cost_default_per_kw</span>（本项）；per-novel
            编辑入口在书籍详情“价格 & 免费章节”小节
          </span>
        </div>
      </FormRow>
      {!isWeb && (
        <FormRow
          label="第三方支付赠送比例"
          hint="支付页面展示依赖此配置。Adapty 实际产品权益需与此比例保持一致。"
        >
          <div className="hstack" style={{ gap: 8 }}>
            <input
              className="input mono"
              value={form.bonusRatioPct}
              onChange={(e) => update({ bonusRatioPct: e.target.value })}
              style={{ width: 80, textAlign: 'right' }}
            />
            <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>%</span>
            <span className="chip chip-info" style={{ marginLeft: 12 }}>
              已与 Adapty 同步
            </span>
          </div>
        </FormRow>
      )}
      <FormRow label="免费章节数" hint="默认 5 · 创建书籍时可覆盖，之后可在书籍信息中调整">
        {/* App 级无对应契约字段（免费章节为 per-novel 配置）→ 只读展示默认 5，不入保存 payload（计划 §五） */}
        <input
          className="input mono"
          value="5"
          readOnly
          disabled
          title="App 级无对应契约字段（免费章节为 per-novel 配置，此处为 books 域默认值展示）· 见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五"
          style={{ width: 80, textAlign: 'right', background: 'var(--bg-subtle)' }}
        />
      </FormRow>
    </Section>
  );
}

/**
 * 小时阅读卡运营参数（ADM-APP-S014 / SRV-READ-019 / SRV-READ-022）—— 契约 AB-运营应用 §4。
 *
 * 三参同属「计价 & BONUS」tab：小时阅读卡是按时长计费的付费阅读能力，与千字单价 / 赠送比例同为
 * App 级货币化参数。三个控件都只改本地表单，随页头「保存」一起走 PATCH /apps/:id（无独立即时提交
 * 端点——契约 §4 是唯一入口，且总开关与两个数值参数必须能在一次审计里看到同一组 before/after）。
 */
/** 主屏配置直接编辑服务端真值，未触碰时不随无关保存覆盖其他运营的配置。 */
function WebHomeScreenSection({
  form,
  update,
  canEdit,
}: {
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
  canEdit: boolean;
}) {
  const markDirty = useMarkDirty();
  const config = form.homeScreenConfig;
  const change = (patch: Partial<HomeScreenConfig>) => {
    if (!config) return;
    update({ homeScreenConfig: { ...config, ...patch }, homeScreenConfigTouched: true });
    markDirty();
  };
  return (
    <Section title="添加到主屏" sub="配置 Web 主屏提示的开关、触发阈值与冷却时间">
      {!config ? (
        <p className="muted">服务端未返回有效的完整主屏配置，刷新或检查配置后再编辑。</p>
      ) : (
        <>
          {(
            [
              ['is_enabled', '主屏提示总开关'],
              ['is_reader_banner_enabled', '阅读页提示条'],
              ['is_payment_success_dialog_enabled', '支付成功提示'],
            ] as const
          ).map(([field, label]) => (
            <FormRow key={field} label={label}>
              <Switch
                checked={config[field]}
                disabled={!canEdit || (field !== 'is_enabled' && !config.is_enabled)}
                onChange={(value) => change({ [field]: value })}
              />
            </FormRow>
          ))}
          {(
            [
              ['chapter_threshold', '章节阈值', '章'],
              ['reader_minutes_threshold', '阅读时长阈值', '分钟'],
              ['cooldown_hours', '提示冷却时间', '小时'],
            ] as const
          ).map(([field, label, unit]) => (
            <FormRow key={field} label={label} hint={`大于 0 的整数（${unit}）`}>
              <input
                className="input"
                type="number"
                min={1}
                step={1}
                value={Number.isNaN(config[field]) ? '' : config[field]}
                disabled={!canEdit || !config.is_enabled}
                onChange={(event) =>
                  change({
                    [field]: event.target.value === '' ? Number.NaN : Number(event.target.value),
                  })
                }
              />
            </FormRow>
          ))}
        </>
      )}
    </Section>
  );
}

function ReadingPassSection({
  form,
  update,
  canEdit,
  unsupported,
}: {
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
  /** 与同页支付渠道开关同口径：写权限不足时控件本身就禁用，而不是只靠页头「保存」拦。 */
  canEdit: boolean;
  /**
   * 本次 DTO 未带三参（Web 先于 Server 发布的窗口）。此时 `handleSave` 会整体省略这三项 ——
   * 若控件仍可编辑，运营改完点保存会**收到成功提示、改动却被静默丢弃**。故窗口内一并禁用并说明。
   */
  unsupported: boolean;
}) {
  // Switch 是 <button>，点击不触发外层 onChange/onInput 的冒泡 → 必须显式 markDirty()，
  // 否则关掉总开关后页头不出现「保存」按钮，改动无从提交。
  const markDirty = useMarkDirty();
  const trialSeconds = Number(form.readingPassTrialSeconds.trim());
  const trialMinutesHint =
    Number.isInteger(trialSeconds) && trialSeconds > 0
      ? `≈ ${Math.round((trialSeconds / 60) * 10) / 10} 分钟`
      : '须为大于 0 的整数秒';
  const unlockDays = Number(form.readingPassUnlockDays.trim());
  const unlockDaysInvalid = !Number.isInteger(unlockDays) || unlockDays < 1;
  return (
    <Section
      title="小时阅读卡"
      sub="ADM-APP-S014 · 无语言独立配置时使用下列 App 默认；语言独立配置五项整份覆盖。每日判定在用户本地 00:00–02:00 窗口内按当时配置求值，已有终态不回收。"
    >
      {unsupported && (
        <div
          className="card-flat"
          style={{
            marginBottom: 10,
            padding: '8px 10px',
            background: 'var(--warn-soft)',
            fontSize: 11.5,
          }}
        >
          <b>暂不可编辑</b>
          <div style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
            服务端本次未返回这三项配置（发布窗口内 Web 已更新、Server 尚未跟上）。此时保存会整体
            省略它们 —— 为免改动被静默丢弃，控件暂时禁用；Server 部署完成后刷新即可编辑。
          </div>
        </div>
      )}
      <FormRow
        label="App 默认阅读卡开关"
        hint="无语言独立配置时，关闭阻止无终态用户的新解锁判定；已有终态不回收。独立语言的开关单独生效，重新开启不触发批量补授。"
      >
        <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
          <Switch
            ariaLabel="App 默认阅读卡开关"
            checked={form.readingPassEnabled}
            disabled={!canEdit}
            onChange={(next) => {
              update({ readingPassEnabled: next });
              markDirty();
            }}
          />
          <span className={`chip ${form.readingPassEnabled ? 'chip-pos' : ''}`}>
            {form.readingPassEnabled ? '已启用' : '已关闭'}
          </span>
          {!form.readingPassEnabled && (
            <span className="chip chip-warn" style={{ fontSize: 10 }}>
              使用 App 默认的语言停止新解锁
            </span>
          )}
        </div>
      </FormRow>
      <FormRow
        label="免费体验卡时长"
        hint="领取时把本值固化进该张卡；改后只影响新领取的卡，已入账的卡仍按其自身时长激活。"
      >
        <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="input mono"
            inputMode="numeric"
            value={form.readingPassTrialSeconds}
            disabled={!canEdit}
            onChange={(e) => update({ readingPassTrialSeconds: e.target.value })}
            style={{ width: 100, maxWidth: '100%', textAlign: 'right' }}
          />
          <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>秒</span>
          <span
            style={{
              fontSize: 11,
              marginLeft: 4,
              color:
                Number.isInteger(trialSeconds) && trialSeconds > 0
                  ? 'var(--fg-muted)'
                  : 'var(--neg)',
            }}
          >
            {trialMinutesHint}
          </span>
        </div>
      </FormRow>
      <FormRow
        label="开启天数（注册自然日龄）"
        hint="以用户注册当天（按用户日切时区）为 D0，判定日与 D0 相差 ≥ 本值天即满足门槛，不要求阅读；默认 2 即注册后第 2 天（D2）起。满足门槛后由每日判定写入终态：尚未付费的用户功能可见，已有成功支付的保持隐藏。改后只影响尚未写入终态的用户，已写入的终态行不改写、不重判。"
      >
        <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="input mono"
            inputMode="numeric"
            value={form.readingPassUnlockDays}
            disabled={!canEdit}
            onChange={(e) => update({ readingPassUnlockDays: e.target.value })}
            style={{ width: 100, maxWidth: '100%', textAlign: 'right' }}
          />
          <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>天</span>
          {unlockDaysInvalid && (
            <span style={{ fontSize: 11, marginLeft: 4, color: 'var(--neg)' }}>
              须为不小于 1 的整数
            </span>
          )}
        </div>
      </FormRow>
      {!form.readingPassReminderSupported && (
        <p style={{ color: 'var(--fg-muted)' }}>
          服务端尚未返回提醒配置，提醒参数暂不可编辑；部署完成后刷新即可使用。
        </p>
      )}
      <ReadingPassReminderFields
        draft={{
          enabled: form.readingPassReminderEnabled,
          time: form.readingPassReminderTime,
          maxCount: form.readingPassReminderMaxCount,
        }}
        disabled={!canEdit || !form.readingPassReminderSupported}
        statusUnknown={!form.readingPassReminderSupported}
        onChange={(patch) => {
          update({
            ...(patch.enabled !== undefined
              ? {
                  readingPassReminderEnabled: patch.enabled,
                  readingPassReminderEnabledTouched: true,
                }
              : {}),
            ...(patch.time !== undefined
              ? {
                  readingPassReminderTime: patch.time,
                  readingPassReminderTimeTouched: true,
                }
              : {}),
            ...(patch.maxCount !== undefined
              ? {
                  readingPassReminderMaxCount: patch.maxCount,
                  readingPassReminderMaxCountTouched: true,
                }
              : {}),
          });
          markDirty();
        }}
      />
    </Section>
  );
}

function NovelPurchaseSection({
  form,
  update,
  canEdit,
  unsupported,
}: {
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
  canEdit: boolean;
  unsupported: boolean;
}) {
  const markDirty = useMarkDirty();
  const levels = ['L1', 'L2', 'L3', 'L4', 'L5'] as const;
  return (
    <Section
      title="书籍买断"
      sub="ADM-APP-S015 · 总开关开启前五个价格档都必须已配买断产品；已授予用户不受关停影响"
    >
      {unsupported && (
        <div
          className="card-flat"
          style={{ marginBottom: 10, padding: '8px 10px', fontSize: 11.5 }}
        >
          服务端本次未返回买断配置，保存会省略这些字段。
        </div>
      )}
      <FormRow
        label="书籍买断总开关"
        hint="缺省关。开启前须配齐 L1–L5。关闭不是全量收口——审核放行成立的 C 端请求方不看本开关（SRV-READ-028）。"
      >
        <div className="hstack" style={{ gap: 10 }}>
          <Switch
            checked={form.novelPurchaseEnabled}
            disabled={!canEdit}
            onChange={(next) => {
              update({ novelPurchaseEnabled: next });
              markDirty();
            }}
          />
          <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            {form.novelPurchaseEnabled ? '已启用' : '已关闭'}
          </span>
        </div>
      </FormRow>
      <FormRow
        label="解锁所需首次付费后天数"
        hint="0 = 当天授予。改小后已满足阈值的用户由 C 端补授。"
      >
        <input
          className="input mono"
          inputMode="numeric"
          value={form.novelPurchaseUnlockDays}
          disabled={!canEdit}
          onChange={(e) => update({ novelPurchaseUnlockDays: e.target.value })}
          style={{ width: 100, textAlign: 'right' }}
        />
      </FormRow>
      {levels.map((level) => (
        <FormRow key={level} label={`${level} 买断产品`} hint="product_metadata.access_level_id">
          <input
            className="input mono"
            value={form.novelPurchaseProducts[level]}
            disabled={!canEdit}
            onChange={(e) =>
              update({
                novelPurchaseProducts: { ...form.novelPurchaseProducts, [level]: e.target.value },
              })
            }
            placeholder="access_level_id"
          />
        </FormRow>
      ))}
    </Section>
  );
}

/**
 * 审核版本号（ADM-APP-S017 / SRV-OPS-008）。**本页是唯一写入入口**：建 App / 复制 App 恒 NULL，
 * 置空是关闭审核版本模式的唯一收口手段。
 */
function StoreReviewVersionSection({
  form,
  update,
  canEdit,
  unsupported,
}: {
  form: AppConfigForm;
  update: (patch: Partial<AppConfigForm>) => void;
  canEdit: boolean;
  unsupported: boolean;
}) {
  const markDirty = useMarkDirty();
  return (
    <Section
      title="审核版本号"
      sub="ADM-APP-S017 · 填入某次送审的客户端版本号即开启审核版本模式；置空即全量关闭"
    >
      {unsupported && (
        <div
          className="card-flat"
          style={{ marginBottom: 10, padding: '8px 10px', fontSize: 11.5 }}
        >
          服务端本次未返回审核版本号，保存会省略该字段。
        </div>
      )}
      <FormRow
        label="审核版本号"
        hint="留空 = 关闭。服务端不解析版本号语义（不比大小、不匹配已发布版本），填错即无请求命中。"
      >
        <input
          className="input mono"
          value={form.storeReviewVersion}
          disabled={!canEdit}
          onChange={(e) => {
            update({ storeReviewVersion: e.target.value });
            markDirty();
          }}
          placeholder="留空 = 关闭"
        />
      </FormRow>
      <div className="card-flat" style={{ padding: '8px 10px', fontSize: 11.5 }}>
        置位期间<strong>命中该版本的全部真实用户同等放行</strong>，不区分审核员与普通用户；
        放行本身在 C 端不报错、不留痕，开没开只能看这里与操作日志。
        <br />
        关闭不是即时收回：放行期间已取到解锁位的身份，客户端小时卡入口按 CLI-COM-012
        单调置位，不随置空立刻收回。
        <br />
        命中审核版本的请求<strong>只返回 4+ 书籍</strong>，命中的真实用户同样看不到其他书。
        <br />
        送审版本向普通用户开放下载前，<strong>必须先置空本项</strong>。
      </div>
    </Section>
  );
}

/** 支付渠道闭集 → 设计稿展示名（契约 AppPaymentMethodResponse.payment_method；PayPal 在闭集外） */
const PAYMENT_CHANNEL_META: Record<
  AppPaymentMethodResponse['payment_method'],
  { name: string; badge: string }
> = {
  IAP: { name: '应用内支付 (IAP)', badge: 'App Store / Google Play' },
  STRIPE: { name: '第三方支付 (Stripe)', badge: '卡支付' },
  PAYERMAX: { name: 'PAYERMAX', badge: '框架通道' },
};

/**
 * 渠道摘要状态文案：n = 已填 placement 的行数 / m = 未软删物理行数。
 * **只讲 placement 配置度、不讲启停** —— 启停真值是通道策略组成员集合（ADM-PAY-S008）。
 */
function formatPaymentMethodSummaryStatus(summary: {
  status: 'unconfigured' | 'active' | 'partial';
  placementScenarioCount: number;
  configuredScenarioCount: number;
}): string {
  switch (summary.status) {
    case 'unconfigured':
      return '未配置';
    case 'active':
      return `已配置 · ${summary.placementScenarioCount}/${summary.configuredScenarioCount} 场景`;
    case 'partial':
      return `部分配置 · ${summary.placementScenarioCount}/${summary.configuredScenarioCount} 场景`;
  }
}

function DetailPayment({ app }: { app: AppRow }) {
  const navigate = useNavigate();
  const setAppId = useAppScope((state) => state.setAppId);
  const { data: methods, isLoading, isError } = useAppPaymentMethods(app.id);

  // 摘要只反映 placement 配置度，**不掺 App 级死列** —— 启停真值在下方策略组面板（ADM-PAY-S008）。
  const paymentMethodSummaries = summarizeAppPaymentMethods(methods);
  const openPaywallForApp = () => {
    setAppId(app.id);
    navigate('/paywall');
  };
  return (
    <Section
      title="支付渠道"
      sub="App 页面仅汇总 IAP / Stripe 渠道状态；各支付场景的 placement 配置请在 Paywall 管理。"
    >
      {isLoading && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
          支付渠道加载中…
        </div>
      )}
      {isError && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--neg)' }}>
          支付渠道加载失败
        </div>
      )}
      {!isLoading &&
        !isError &&
        paymentMethodSummaries.map((summary) => {
          const meta = PAYMENT_CHANNEL_META[summary.paymentMethod];
          const status = formatPaymentMethodSummaryStatus(summary);
          return (
            <FormRow key={summary.paymentMethod} label={meta.name} hint={meta.badge}>
              <div className="hstack" style={{ gap: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  <span className={summary.status === 'active' ? 'delta-pos' : undefined}>
                    {status}
                  </span>
                </span>
                <div style={{ flex: 1 }} />
                <button className="btn btn-sm" onClick={openPaywallForApp}>
                  <Icons.IconCog size={11} />
                  Paywall
                </button>
              </div>
            </FormRow>
          );
        })}
      <div style={{ padding: '14px 0', display: 'flex', gap: 12, alignItems: 'center' }}>
        <Icons.IconShield size={14} style={{ color: 'var(--info)' }} />
        <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
          支付产品配置由 <span className="mono strong">Adapty</span>{' '}
          后台承接；本页仅展示渠道配置行汇总；支付页面是否上线请在 Paywall 查看。
        </span>
      </div>
    </Section>
  );
}

/** 任务奖励视图行：契约 task_center_rewards 字段 → 设计稿分组卡片（values 为可编辑奖励额） */
interface TaskItemVM {
  id: string;
  name: string;
  sub?: string;
  isList: boolean;
  values: number[];
  onChangeAt: (index: number, value: number) => void;
}

/**
 * ADM-APP-S016 —— 任务开放与排序配置。
 *
 * 契约 `03-数据字典/70-产品配置.md` §1.2 / §1.2.1：
 * - 可配置对象恒为 **8 项闭集**，界面不提供「新增任务」入口（新增任务类型走服务端发版）
 * - **顺序由数组下标承载**，不存排序整数 —— 故这里直接重排数组，不写任何 order 字段
 * - 关闭只影响 C 端展示与领取，**不动进度事实**（§1.2.2），文案里对运营讲清楚，
 *   否则「关掉会不会把用户已完成的记录清了」这个问题会反复回到后端
 *
 * 排序走 `~/ui/SortableList`（自研 HTML5 拖拽，无第三方库）：拖拽只是鼠标快捷方式，
 * 上 / 下移按钮恒在、键盘可达。需求原文只要求「顺序以数组元素顺序表达」，未规定交互形态。
 */
function TaskDisplayConfig({
  form,
  update,
  canEdit,
}: {
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
  /** 配置编辑权限（root/admin）。operator / viewer 保存被服务端与「保存」按钮双拒 ——
   * 控件若仍可点，他们能改出本地态并触发「未保存更改」拦截流程，而那份改动永远存不下去 */
  canEdit: boolean;
}) {
  const entries = form.taskDisplay;
  const unsupported = !form.taskDisplaySupported;
  // 无编辑权限与「服务端版本不支持」在交互上同档：控件一律禁用
  const readOnly = unsupported || !canEdit;
  const enabledCount = entries.filter((e) => e.is_enabled).length;
  // 坏数据**且运营还没确认修复** → 本次保存不提交该键
  const malformedPending = form.taskDisplayMalformed && !form.taskDisplayTouched;
  // 上/下移与拖拽是 <button> / DnD 事件、开关是 Switch（内部也是 <button>）—— 都**不触发**外层
  // onChange/onInput 冒泡，而详情页的 dirty 态正是靠那个冒泡建立的（见 DetailPricing 同款注释）。
  // 不显式 markDirty() 的话：运营调完顺序、关掉任务，「保存」按钮根本不出现，改动静默丢失。
  const markDirty = useMarkDirty();
  const applyDisplay = (next: TaskCenterTaskDisplayEntry[]) => {
    update({ taskDisplay: next, taskDisplayTouched: true });
    markDirty();
  };

  return (
    <Section
      title="任务开放与排序"
      sub="按 App 配置任务中心普通任务的开放与展示顺序；顺序即列表顺序。关闭只影响 C 端展示与领取，不清除用户已有进度与完成记录；激励广告关闭时停止新准入，已有名额与回调履约不受影响"
    >
      {unsupported && (
        <div style={{ padding: '10px 0', fontSize: 11, color: 'var(--warn)' }}>
          当前服务端尚未确认支持八项任务配置，本次保存将不提交此项（其余配置照常可改）。等服务端发布后刷新即可编辑。
        </div>
      )}
      {malformedPending && (
        <div className="hstack" style={{ padding: '10px 0', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--neg)' }}>
            库中该配置不合规范（可能被直接改库）。C 端此时按<strong>未列出的任务一律关闭</strong>
            处理。 下方显示的是<strong>建议的修复值</strong>，不是当前生效配置 ——
            在你确认前不会随保存写回，以免把运营已关闭的任务又打开。
          </span>
          <button
            type="button"
            className="btn"
            disabled={readOnly}
            onClick={() => applyDisplay(entries)}
          >
            按上方配置修复
          </button>
        </div>
      )}
      <div className="hstack" style={{ justifyContent: 'space-between', padding: '10px 0' }}>
        <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
          每日签到走独立签到面板，不在本配置内
        </span>
        <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
          已开放 {enabledCount} / {entries.length}
        </span>
      </div>
      <SortableList
        items={entries}
        getKey={(entry) => entry.task_slug}
        itemLabel={(entry) => TASK_CENTER_TASK_DISPLAY_LABELS[entry.task_slug]}
        onReorder={applyDisplay}
        disabled={readOnly}
        renderContent={(entry) => (
          <span style={{ fontSize: 12.5, opacity: entry.is_enabled ? 1 : 0.6 }}>
            {TASK_CENTER_TASK_DISPLAY_LABELS[entry.task_slug]}
          </span>
        )}
        renderActions={(entry) => (
          <Switch
            ariaLabel={`开放 ${TASK_CENTER_TASK_DISPLAY_LABELS[entry.task_slug]}`}
            checked={entry.is_enabled}
            disabled={readOnly}
            onChange={(next) =>
              applyDisplay(
                entries.map((e) =>
                  e.task_slug === entry.task_slug ? { ...e, is_enabled: next } : e,
                ),
              )
            }
          />
        )}
      />
    </Section>
  );
}

function DetailTasks({
  form,
  update,
  canEdit,
}: {
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
  canEdit: boolean;
}) {
  const rewards = form.taskRewards;
  const setRewards = (next: TaskCenterRewards) => update({ taskRewards: next });
  // 非负整数解析：空/非法回 0（输入过程即时回写，提交前 Zod 再兜底）
  const toInt = (s: string) => {
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  };
  const fixedItem = (key: keyof TaskCenterRewards['fixed'], name: string): TaskItemVM => ({
    id: key,
    name,
    isList: false,
    values: [rewards.fixed[key]],
    onChangeAt: (_i, v) => setRewards({ ...rewards, fixed: { ...rewards.fixed, [key]: v } }),
  });
  // 字段映射（契约 → 设计稿文案）：fixed.app_review 应用评分 / enable_notification 开启通知 /
  // account_login 账号登录 / bind_email 绑定邮箱 / daily_first_recharge 每日首充；
  // tiered.daily_signin 每日签到 7 天 / reading_time 阅读时长（minutes 档位只读、reward 可编辑）/
  // unlock_chapter 解锁章节（chapters 档位只读、reward 可编辑）
  const groups: { type: string; items: TaskItemVM[] }[] = [
    {
      type: '一次性',
      items: [
        fixedItem('app_review', '应用评分'),
        fixedItem('enable_notification', '开启通知'),
        fixedItem('account_login', '账号登录'),
        fixedItem('bind_email', '绑定邮箱'),
      ],
    },
    {
      type: '每日',
      items: [
        fixedItem('daily_first_recharge', '每日首充'),
        {
          id: 'daily_signin',
          name: '每日签到 (7 天)',
          isList: true,
          values: rewards.tiered.daily_signin,
          onChangeAt: (i, v) =>
            setRewards({
              ...rewards,
              tiered: {
                ...rewards.tiered,
                daily_signin: rewards.tiered.daily_signin.map((x, j) => (j === i ? v : x)),
              },
            }),
        },
        {
          id: 'reading_time',
          name: `阅读时长 (${rewards.tiered.reading_time.length} 档)`,
          sub: `${rewards.tiered.reading_time.map((t) => t.minutes).join('/')} 分钟`,
          isList: true,
          values: rewards.tiered.reading_time.map((t) => t.reward),
          onChangeAt: (i, v) =>
            setRewards({
              ...rewards,
              tiered: {
                ...rewards.tiered,
                reading_time: rewards.tiered.reading_time.map((t, j) =>
                  j === i ? { ...t, reward: v } : t,
                ),
              },
            }),
        },
        {
          id: 'unlock_chapter',
          name: `解锁章节 (${rewards.tiered.unlock_chapter.length} 档)`,
          sub: `${rewards.tiered.unlock_chapter.map((t) => t.chapters).join('/')} 章`,
          isList: true,
          values: rewards.tiered.unlock_chapter.map((t) => t.reward),
          onChangeAt: (i, v) =>
            setRewards({
              ...rewards,
              tiered: {
                ...rewards.tiered,
                unlock_chapter: rewards.tiered.unlock_chapter.map((t, j) =>
                  j === i ? { ...t, reward: v } : t,
                ),
              },
            }),
        },
      ],
    },
  ];
  return (
    <>
      <TaskDisplayConfig form={form} update={update} canEdit={canEdit} />
      <Section
        title="任务中心 BONUS 配置"
        sub="任务类型为系统预置闭集，仅支持配置奖励金额，不支持新增类型"
      >
        {groups.map((g) => (
          <div key={g.type} style={{ padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
            <div className="hstack" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontWeight: 500, fontSize: 12 }}>{g.type}任务</span>
              <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>{g.items.length} 项</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {g.items.map((it) => (
                <div
                  key={it.id}
                  className="hstack"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    gap: 10,
                  }}
                >
                  <Icons.IconStar size={13} style={{ color: 'var(--warn)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{it.name}</div>
                    {it.sub && (
                      <div style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>{it.sub}</div>
                    )}
                  </div>
                  {it.isList ? (
                    <div className="hstack mono" style={{ gap: 4, fontSize: 11 }}>
                      {it.values.map((v, i) => (
                        <input
                          key={i}
                          value={v}
                          onChange={(e) => it.onChangeAt(i, toInt(e.target.value))}
                          className="input"
                          style={{
                            width: 38,
                            height: 24,
                            padding: '0 6px',
                            fontSize: 11,
                            textAlign: 'center',
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <input
                      value={it.values[0] ?? 0}
                      onChange={(e) => it.onChangeAt(0, toInt(e.target.value))}
                      className="input mono"
                      style={{ width: 68, height: 24, fontSize: 11, textAlign: 'right' }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </Section>
    </>
  );
}

function DetailLink({
  form,
  update,
}: {
  form: AppConfigForm;
  update: (p: Partial<AppConfigForm>) => void;
}) {
  const schema = form.linkSchema || '<schema>';
  return (
    <Section title="App Link Schema" sub="浏览器访问该 schema 可唤起 App。推送内容也复用该前缀。">
      <FormRow label="Schema 前缀">
        <div className="hstack" style={{ gap: 0 }}>
          <input
            className="input mono"
            value={form.linkSchema}
            onChange={(e) => update({ linkSchema: e.target.value })}
            placeholder="novellea"
            style={{ borderRadius: 'var(--radius) 0 0 var(--radius)', maxWidth: 280 }}
          />
          <span
            style={{
              padding: '0 10px',
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              border: '1px solid var(--border)',
              borderLeft: 0,
              borderRadius: '0 var(--radius) var(--radius) 0',
              background: 'var(--bg-subtle)',
              color: 'var(--fg-muted)',
              fontSize: 12,
            }}
            className="mono"
          >
            ://
          </span>
        </div>
      </FormRow>
      <FormRow label="示例链接" hint="推送 / Web 唤起会使用此模板">
        <div className="vstack" style={{ gap: 6 }}>
          <code
            style={{
              padding: '4px 8px',
              background: 'var(--bg-subtle)',
              borderRadius: 4,
              fontSize: 11,
            }}
          >
            {schema}://book/bk_8001?utm=push
          </code>
          <code
            style={{
              padding: '4px 8px',
              background: 'var(--bg-subtle)',
              borderRadius: 4,
              fontSize: 11,
            }}
          >
            {schema}://pay/recharge?promo=summer
          </code>
        </div>
      </FormRow>
    </Section>
  );
}

function DetailDanger({
  app,
  dto,
  setDialog,
  onSetActive,
  canManage,
}: {
  app: AppRow;
  dto: AppResponse;
  setDialog: SetDialog;
  onSetActive: SetActive;
  canManage: boolean;
}) {
  return (
    <Section title="高级操作" sub="以下操作会影响用户访问 / 客户端配置，请谨慎">
      <FormRow label="复制 App" hint="生成新 App ID，配置内容保持一致，Banner 位默认下架">
        <button
          className="btn"
          disabled={!canManage}
          title={canManage ? undefined : MANAGE_FORBIDDEN_TITLE}
          onClick={() => setDialog({ kind: 'copy-app', source: app })}
        >
          <Icons.IconCopy size={12} />
          复制为新 App
        </button>
      </FormRow>
      <FormRow label="暂停运营" hint="客户端请求将被服务端拒绝，已购权益保留">
        {dto.is_active ? (
          <button
            className="btn btn-danger"
            disabled={!canManage}
            title={canManage ? undefined : MANAGE_FORBIDDEN_TITLE}
            onClick={() =>
              setDialog({
                kind: 'confirm',
                config: buildAppLifecycleConfirm(app, 'pause', () => onSetActive(app, false)),
              })
            }
          >
            <Icons.IconPause size={12} />
            暂停
          </button>
        ) : (
          <button
            className="btn"
            disabled={!canManage}
            title={canManage ? undefined : MANAGE_FORBIDDEN_TITLE}
            onClick={() =>
              setDialog({
                kind: 'confirm',
                config: buildAppLifecycleConfirm(app, 'resume', () => onSetActive(app, true)),
              })
            }
          >
            <Icons.IconPlay size={12} />
            已暂停 · 恢复运营
          </button>
        )}
      </FormRow>
      <FormRow label="导出配置" hint="导出当前 App 的全部运营配置 (JSON)">
        <button className="btn" onClick={() => void exportAppConfigJson(app)}>
          <Icons.IconDownload size={12} />
          导出 JSON
        </button>
      </FormRow>
    </Section>
  );
}

function Legend({
  dot,
  label,
  val,
  extra,
}: {
  dot: string;
  label: string;
  val: string;
  extra: string;
}) {
  return (
    <div className="hstack" style={{ gap: 8, fontSize: 12 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: dot }} />
      <span style={{ flex: 1, color: 'var(--fg-muted)' }}>{label}</span>
      <span className="mono strong">{val}</span>
      <span className="mono" style={{ color: 'var(--fg-muted)' }}>
        {extra}
      </span>
    </div>
  );
}
